import React, { useState, useEffect, useRef } from "react";
import {
  watchJob, updateJob, cancelJob, restoreJob, deleteJobHard, releaseJobToPool,
  ownerConfirmGuard, logAudit, getJobAuditLog, createJob, upsertLocationFromStop,
  getCompany, getBundleJobs, updateBundle, jobDisplayNumber,
} from "../../services/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import PlacesAutocomplete from "../PlacesAutocomplete";
import PhoneInput from "../PhoneInput";
import SalesRepAutocomplete, { useKnownReps } from "../SalesRepAutocomplete";
import { formatPhone, telHref } from "../../utils/phone";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { hawbLabel, hawbNumLabel } from "../../services/hawbLabel";
import { guardOwnsCar, vaultNightsBetween } from "../../services/billing";

const STATUS_LABELS = {
  intake:     { label: "Intake",      chip: "chip-amber" },
  pool:       { label: "In pool",     chip: "chip-gray"  },
  grabbed:    { label: "Grabbed",     chip: "chip-blue"  },
  confirmed:  { label: "Confirmed",   chip: "chip-blue"  },
  inProgress: { label: "In progress", chip: "chip-teal"  },
  complete:   { label: "Complete",    chip: "chip-green" },
  cancelled:  { label: "Cancelled",   chip: "chip-red"   },
};

const JOB_TYPE_LABELS = { inbound: "Inbound", outbound: "Outbound", roundtrip: "Round trip", direct: "Direct" };

function uid() { return Math.random().toString(36).slice(2, 9); }

// Keep stop types in sync with jobType. Trust the stop's own `type` to
// identify the airport — do NOT relabel by position. A non-airport stop in
// the last position of an outbound should stay a pickup; the airport is the
// one explicitly tagged `type: "airport"` at seed time.
function normalizeStopTypes(stops, jobType) {
  if (!Array.isArray(stops) || stops.length === 0) return stops;
  return stops.map((s, i, arr) => {
    if (jobType === "outbound") {
      return { ...s, type: s.type === "airport" ? "airport" : "pickup" };
    }
    if (jobType === "inbound") {
      return { ...s, type: s.type === "airport" ? "airport" : "delivery" };
    }
    if (jobType === "roundtrip") {
      // Mixed run — each stop keeps its own pickup/delivery type.
      if (s.type === "airport") return s;
      return { ...s, type: s.type === "pickup" ? "pickup" : "delivery" };
    }
    if (jobType === "direct") {
      if (arr.length === 1) return { ...s, type: "pickup" };
      if (i === 0) return { ...s, type: "pickup" };
      if (i === arr.length - 1) return { ...s, type: "delivery" };
      return { ...s, type: "pickup" };
    }
    return { ...s, type: s.type || "pickup" };
  });
}

export default function JobDetailModal({ jobId, onClose, companyId }) {
  const { userProfile } = useAuth();
  const [job, setJob]         = useState(null);
  const [tab, setTab]         = useState("details");
  // Phone width: compact summary, slim airport line, ⋯ sheet for Cancel/Delete.
  const [isPhone, setIsPhone] = useState(typeof window !== "undefined" && window.innerWidth < 700);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [guards, setGuards]   = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [companyDoc, setCompanyDoc] = useState(null);
  // Locations list — needed to look up saved contacts for each stop.
  // Indexed by id for O(1) lookup in the render path.
  const [locations, setLocations] = useState([]);
  const locationsById = React.useMemo(() => {
    const m = {};
    for (const l of locations) m[l.id] = l;
    return m;
  }, [locations]);
  const [auditLog, setAuditLog] = useState([]);
  // Roster of past reps (deduped by broker+name) — drives the
  // autocomplete suggestions on the Sales rep field. Loaded once when the
  // modal opens via the shared useKnownReps hook.
  const knownReps = useKnownReps(companyId);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [showMovePkgs, setShowMovePkgs]     = useState(false);
  const [showSplit, setShowSplit]           = useState(false);
  // Sibling jobs sharing this job's bundleId (DAI multi-detail group).
  // Loaded once when entering edit mode so we can offer "apply rep change
  // to all N jobs in this bundle" without an extra Firestore round-trip
  // on every keystroke.
  const [bundleSiblings, setBundleSiblings] = useState([]);
  const [applyRepToBundle, setApplyRepToBundle] = useState(false);
  // Distinguishes "still waiting for the listener's first emit" (true)
  // from "listener fired but the doc doesn't exist" (false + job null).
  // Without this, the "Loading job..." spinner stayed up forever when an
  // invoice referenced a deleted/never-created job (e.g. a Ferrari
  // Forward-bundle invoice).
  const [resolved, setResolved] = useState(false);
  const navigate = useNavigate();
  // Invoices billed for this job (one per guard), shown in the Invoice
  // panel with a link to the Invoices page.
  const [jobInvoices, setJobInvoices] = useState([]);
  const invoiceCompanyId = companyId || userProfile?.companyId;
  useEffect(() => {
    if (!invoiceCompanyId || !jobId) return;
    let cancelled = false;
    getDocs(query(
      collection(db, "invoices"),
      where("companyId", "==", invoiceCompanyId),
      where("jobId", "==", jobId),
    ))
      .then((snap) => {
        if (cancelled) return;
        setJobInvoices(snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0)));
      })
      .catch((err) => console.warn("Couldn't load invoices for job", err));
    return () => { cancelled = true; };
  }, [invoiceCompanyId, jobId]);

  // Live job watcher
  useEffect(() => {
    return watchJob(jobId, (data) => {
      setResolved(true);
      setJob(data);
      // Lazy backfill: if an old job doesn't have numGuards, set it to the
      // assigned count (or 1) so the Inbox "X/Y assigned" display becomes honest.
      if (data && data.numGuards == null) {
        const backfill = Math.max(1, data.guardAssignments?.length || 1);
        updateJob(jobId, { numGuards: backfill }).catch(() => {});
      }
      // Seed form when job loads (but not while user is actively editing)
      setForm((prev) => editing ? prev : {
        jobNumber:         data?.jobNumber         || "",
        jobType:           data?.jobType           || "outbound",
        brokerId:          data?.brokerId          || "",
        brokerName:        data?.brokerName        || "",
        dispatcherName:    data?.dispatcherName    || "",
        salesRepName:      data?.salesRepName      || "",
        salesRepPhone:     data?.salesRepPhone     || "",
        airportCode:       data?.airportCode       || "",
        scheduledDate:     data?.scheduledDate     || "",
        scheduledTime:     data?.scheduledTime     || "",
        notes:             data?.notes             || "",
        ownerNotes:        data?.ownerNotes        || "",
        billingClockStart: data?.billingClockStart || "",
        billingClockEnd:   data?.billingClockEnd   || "",
        guardAssignments:  data?.guardAssignments  || [],
        numGuards:         data?.numGuards         || Math.max(1, data?.guardAssignments?.length || 1),
        stops:             (data?.stops    || []).map((s) => s.id ? s : { ...s, id: uid() }),
        packages:          (data?.packages || []).map((p) => p.id ? p : { ...p, id: uid() }),
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Assignable people: guards AND owners. Owners can work jobs too
  // (the app has a "Switch to guard mode"), so they need to be
  // selectable here. Owner entries are flagged so the dropdown can
  // label them "(owner)".
  useEffect(() => {
    if (!companyId) return;
    getDocs(query(
      collection(db, "users"),
      where("companyId", "==", companyId),
      where("role", "in", ["guard", "owner"])
    )).then((snap) => {
      const people = snap.docs.map((d) => {
        const data = d.data();
        return { id: d.id, ...data, _isOwner: data.role === "owner" };
      });
      // Sort guards first, then owners; alphabetical within each.
      people.sort((a, b) => {
        if (!!a._isOwner !== !!b._isOwner) return a._isOwner ? 1 : -1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
      setGuards(people);
    });
  }, [companyId]);

  // Brokers list — needed so the owner can switch the broker on an existing job.
  useEffect(() => {
    if (!companyId) return;
    getDocs(query(
      collection(db, "brokers"),
      where("companyId", "==", companyId),
    )).then((snap) => setBrokers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    // Company doc — needed for the airport stop's "Use hub address" button
    // and for any other place that references hub config in this modal.
    getCompany(companyId).then(setCompanyDoc).catch(() => {});
    // Locations — used to surface saved contacts under each stop. We
    // load fresh on modal open so any contacts added in another tab
    // (Locations page) are reflected immediately.
    getDocs(query(
      collection(db, "locations"),
      where("companyId", "==", companyId),
    )).then((snap) => setLocations(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [companyId]);

  // Audit log — load when tab opens
  useEffect(() => {
    if (tab === "log") getJobAuditLog(jobId).then(setAuditLog);
  }, [tab, jobId]);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function handleDiscard() {
    setEditing(false);
    setError("");
    setForm({
      jobNumber:         job?.jobNumber         || "",
      jobType:           job?.jobType           || "outbound",
      brokerId:          job?.brokerId          || "",
      brokerName:        job?.brokerName        || "",
      dispatcherName:    job?.dispatcherName    || "",
      salesRepName:      job?.salesRepName      || "",
      salesRepPhone:     job?.salesRepPhone     || "",
      airportCode:       job?.airportCode       || "",
      scheduledDate:     job?.scheduledDate     || "",
      scheduledTime:     job?.scheduledTime     || "",
      notes:             job?.notes             || "",
      ownerNotes:        job?.ownerNotes        || "",
      billingClockStart: job?.billingClockStart || "",
      billingClockEnd:   job?.billingClockEnd   || "",
      guardAssignments:  job?.guardAssignments  || [],
      numGuards:         job?.numGuards         || Math.max(1, job?.guardAssignments?.length || 1),
      stops:             (job?.stops    || []).map((s) => s.id ? s : { ...s, id: uid() }),
      packages:          (job?.packages || []).map((p) => p.id ? p : { ...p, id: uid() }),
    });
  }

  // Esc behavior: if currently editing, exit edit mode (discards unsaved
  // changes) instead of closing the whole modal. Otherwise close the modal.
  // Two-step lets a stray keypress not blow away in-progress edits AND not
  // leave the modal stuck open.
  useEscapeKey(() => {
    if (editing) handleDiscard();
    else onClose?.();
  });

  // Load bundle siblings whenever the job's bundleId changes. The check
  // includes editable status so we only count jobs the bundle-edit could
  // actually update (skipping invoiced ones the bulk update would refuse
  // anyway).
  useEffect(() => {
    if (!job?.bundleId || !companyId) {
      setBundleSiblings([]);
      return;
    }
    let cancelled = false;
    getBundleJobs(companyId, job.bundleId).then((jobs) => {
      if (cancelled) return;
      // Sibling = any job in the bundle that isn't this one. Editable
      // siblings are the ones updateBundle would actually rewrite.
      setBundleSiblings(jobs.filter((j) => j.id !== jobId));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [job?.bundleId, companyId, jobId]);

  // Reset the "apply rep to bundle" checkbox each time we enter edit mode
  // so a stale yes from last time doesn't silently fan out.
  useEffect(() => {
    if (editing) setApplyRepToBundle(false);
  }, [editing]);

  // Editable siblings — anything still in pre-invoice land. Newly created
  // jobs default to invoiceStatus="unpaid" (a billing-readiness flag, not
  // an "already billed" marker), so we explicitly include that here. The
  // truly-locked statuses are the ones an actual invoice has progressed
  // through: pending (draft saved), invoiced, sent, paid. Draft is also
  // safe to edit since the invoice hasn't been finalized.
  const LOCKED_INVOICE_STATUSES = new Set(["pending", "invoiced", "sent", "paid"]);
  const editableSiblings = bundleSiblings.filter(
    (j) => !LOCKED_INVOICE_STATUSES.has(j.invoiceStatus)
  );

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      // Drop any assignments beyond the new numGuards count (if user shrank it).
      // Then merge with the live job snapshot to preserve fields that the
      // ReviewBilling modal owns (like `endTime` for per-guard split times) —
      // if this form was seeded before a recent draft save, the form's copy
      // of guardAssignments might miss those fields, and saving notes here
      // would otherwise silently overwrite them.
      const trimmed = (form.guardAssignments || [])
        .filter((g) => g.guardNumber <= form.numGuards)
        .map((g) => {
          const live = (job.guardAssignments || []).find((x) => x.guardNumber === g.guardNumber);
          return {
            ...live,
            ...g,
            endTime: g.endTime != null && g.endTime !== "" ? g.endTime : (live?.endTime || ""),
          };
        });
      const guardIds = trimmed.map((g) => g.guardId).filter(Boolean);

      // Keep workflow status consistent with the assignment, otherwise
      // the Job Inbox self-heal reverts it. For an active (not yet
      // worked/billed) job: assigning ≥1 guard → "confirmed" (the owner
      // directly assigned it); removing all guards → back to "pool".
      // Terminal/in-progress statuses (inProgress/complete/cancelled)
      // and already-billed jobs are left untouched.
      const REASSIGNABLE = new Set(["intake", "pool", "grabbed", "confirmed"]);
      let nextStatus = job.status;
      if (REASSIGNABLE.has(job.status)) {
        nextStatus = guardIds.length > 0 ? "confirmed" : "pool";
      }
      // Use form values (the user may have just changed jobType / airportCode)
      const effJobType  = form.jobType  || job.jobType;
      const effAirport  = form.airportCode || job.airportCode;
      // Guarantee the airport stop exists for inbound/outbound (auto-seed
      // from airportCode if missing); strip it on direct.
      let stopsToSave = [...(form.stops || [])];
      if (effJobType === "direct") {
        stopsToSave = stopsToSave.filter((s) => s.type !== "airport");
      } else if (effJobType === "outbound" || effJobType === "inbound") {
        if (!stopsToSave.some((s) => s.type === "airport")) {
          const airportStop = {
            id: uid(),
            locationName: effAirport || "CLT",
            address: "",
            lat: null, lng: null,
            type: "airport",
            scheduledTime: "",
          };
          if (effJobType === "outbound") stopsToSave.push(airportStop);
          else                            stopsToSave.unshift(airportStop);
        } else {
          // Enforce position + sync airport label with code
          stopsToSave = stopsToSave.map((s) =>
            s.type === "airport" && effAirport ? { ...s, locationName: effAirport } : s
          );
          const airportIdx = stopsToSave.findIndex((s) => s.type === "airport");
          if (effJobType === "outbound" && airportIdx !== stopsToSave.length - 1) {
            const [a] = stopsToSave.splice(airportIdx, 1);
            stopsToSave.push(a);
          } else if (effJobType === "inbound" && airportIdx !== 0) {
            const [a] = stopsToSave.splice(airportIdx, 1);
            stopsToSave.unshift(a);
          }
        }
      }
      const normalizedStops = normalizeStopTypes(stopsToSave, effJobType);
      // Drop orphaned packages' stopId if that stop was removed
      const stopIdSet = new Set(normalizedStops.map((s) => s.id));
      const cleanedPkgs = (form.packages || []).map((p) =>
        p.stopId && !stopIdSet.has(p.stopId) ? { ...p, stopId: null } : p
      );
      // If the owner ticked "apply to bundle", fan the rep values out to
      // every other job sharing this bundleId BEFORE saving the current
      // job. We do NOT gate on whether the rep field changed on this
      // job — the common workflow is "this job already has the right rep,
      // push it to the bundle", so we treat the checkbox as a force-
      // apply action whose source of truth is whatever's in form right
      // now. Order: siblings first so a partial failure leaves the
      // current job untouched (cleaner rollback story).
      if (applyRepToBundle && job.bundleId) {
        const r = await updateBundle(companyId, job.bundleId, {
          salesRepName:  form.salesRepName || "",
          salesRepPhone: form.salesRepPhone || "",
        });
        await logAudit({
          companyId,
          action: `Rep applied across DAI bundle · ${form.salesRepName || "(blank)"} · ${r.updated} job(s) updated, ${r.skipped} skipped (already invoiced)`,
          userId:   userProfile.id,
          userName: userProfile.name,
        });
      }

      // The broker picker is driven by brokerId, so a job whose stored
      // brokerName disagrees with its id (MAKY26-4959 carried "Orion" — the
      // dispatcher — against Malca's rate card) shows the RIGHT broker in the
      // editor and saves the WRONG name straight back: there's no onChange to
      // correct it. The rate card is the source of truth, so re-derive the
      // name from the id on every save. Falls back to the typed name only
      // when the id resolves to nothing.
      const pickedBroker = brokers.find((x) => x.id === form.brokerId);
      await updateJob(jobId, {
        jobNumber:         form.jobNumber || "",
        jobType:           effJobType,
        brokerId:          form.brokerId || "",
        brokerName:        pickedBroker?.name || form.brokerName || "",
        dispatcherName:    form.dispatcherName || "",
        salesRepName:      form.salesRepName || "",
        salesRepPhone:     form.salesRepPhone || "",
        airportCode:       effJobType === "direct" ? "" : (effAirport || ""),
        scheduledDate:     form.scheduledDate || "",
        scheduledTime:     form.scheduledTime || "",
        notes:             form.notes,
        ownerNotes:        form.ownerNotes || "",
        billingClockStart: form.billingClockStart,
        billingClockEnd:   form.billingClockEnd,
        guardAssignments:  trimmed,
        numGuards:         form.numGuards,
        guardIds,
        status:            nextStatus,
        stops:             normalizedStops,
        packages:          cleanedPkgs,
      });
      // Auto-save each real stop as a reusable Location. Fire-and-forget.
      normalizedStops.forEach((s) => {
        upsertLocationFromStop(companyId, s).catch(() => {});
      });
      await logAudit({
        jobId, companyId,
        action:   "Job updated by owner",
        userId:   userProfile.id,
        userName: userProfile.name,
      });
      setEditing(false);
    } catch (e) {
      setError("Failed to save changes. Please try again.");
    }
    setSaving(false);
  }

  async function handleRelease() {
    await releaseJobToPool(jobId);
    await logAudit({ jobId, companyId, action: "Released to pool by owner", userId: userProfile.id, userName: userProfile.name });
  }

  async function handleConfirm() {
    await ownerConfirmGuard(jobId);
    await logAudit({ jobId, companyId, action: "Guard confirmed by owner", userId: userProfile.id, userName: userProfile.name });
  }

  async function handleCancel() {
    if (!window.confirm("Cancel this job?")) return;
    await cancelJob(jobId, "Cancelled by owner");
    await logAudit({ jobId, companyId, action: "Job cancelled by owner", userId: userProfile.id, userName: userProfile.name });
  }

  async function handleRestore() {
    if (!window.confirm("Restore this job? It goes back on the schedule.")) return;
    try {
      await restoreJob({ ...job, id: jobId });
      await logAudit({ jobId, companyId, action: "Job restored by owner (un-cancelled)", userId: userProfile.id, userName: userProfile.name });
    } catch (e) {
      alert(e?.message || String(e));
    }
  }
  const canRestore = job?.status === "cancelled" && !job?.combinedInto;

  /**
   * Hard-delete (test-data cleanup). Two-step confirm so a misclick can't
   * wipe a real job: first window.confirm, then a typed-string match.
   * Logs audit BEFORE the delete so the trail survives even though the job doc won't.
   */
  async function handleDelete() {
    const label = job.jobNumber || job.brokerName || "this job";
    if (!window.confirm(`Permanently delete ${label}?\n\nThis is for test data only — for real jobs use Cancel instead.\nThe job and its draft invoices will be removed. Audit-log entries are kept.`)) return;
    const typed = window.prompt(`To confirm deletion of ${label}, type DELETE in caps:`);
    if (typed !== "DELETE") return;
    try {
      await logAudit({
        jobId, companyId,
        action: `Job hard-deleted by owner · ${label}`,
        userId: userProfile.id, userName: userProfile.name,
      });
      await deleteJobHard(jobId, companyId);
      onClose?.();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed: " + (err?.message || err));
    }
  }

  function handleGuardAssign(guardNumber, guardId) {
    const guard = guards.find((g) => g.id === guardId);
    const rest  = form.guardAssignments.filter((g) => g.guardNumber !== guardNumber);
    if (guardId) {
      setField("guardAssignments", [...rest, {
        guardNumber,
        guardId,
        guardName: guard?.name || "",
        payRate:   guard?.defaultPayRate || "",
      }]);
    } else {
      setField("guardAssignments", rest);
    }
  }

  function handlePayRate(guardNumber, payRate) {
    setField("guardAssignments", form.guardAssignments.map((g) =>
      g.guardNumber === guardNumber ? { ...g, payRate: parseFloat(payRate) || 0 } : g
    ));
  }

  // Own car (bills mileage) vs rides along (time only). Default follows the
  // legacy lead/chase rule (G1/G3 drive) until the owner overrides it.
  function handleVehicle(guardNumber, ownCar) {
    setField("guardAssignments", form.guardAssignments.map((g) =>
      g.guardNumber === guardNumber ? { ...g, ownCar } : g
    ));
  }

  // Toggle whether a guard covers a stop. No `guardNumbers` on a stop means
  // every guard covers it (the default); the first toggle writes the explicit
  // list. A stop must keep at least one guard.
  function toggleStopGuard(stop, n) {
    const all = Array.from({ length: form.numGuards || 1 }, (_, k) => k + 1);
    const current = (Array.isArray(stop.guardNumbers) && stop.guardNumbers.length) ? stop.guardNumbers : all;
    let next;
    if (current.includes(n)) {
      next = current.filter((x) => x !== n);
      if (next.length === 0) return;
    } else {
      next = [...current, n].sort((a, b) => a - b);
    }
    updateStop(stop.id, "guardNumbers", next);
  }

  // ── Stops / packages editing ─────────────────────────────
  function addStop() {
    const newStop = {
      id: uid(), locationName: "", address: "",
      lat: null, lng: null, type: "pickup", scheduledTime: "",
    };
    setForm((f) => {
      const stops = [...(f.stops || [])];
      if (job.jobType === "outbound") {
        const lastIdx = stops.length - 1;
        if (stops[lastIdx]?.type === "airport") {
          stops.splice(lastIdx, 0, newStop);
        } else {
          stops.push(newStop);
          if (!stops.some((s) => s.type === "airport")) {
            stops.push({
              id: uid(), locationName: job.airportCode || "Airport",
              address: "", lat: null, lng: null, type: "airport", scheduledTime: "",
            });
          }
        }
      } else if (job.jobType === "inbound") {
        if (stops.length === 0) {
          stops.push({
            id: uid(), locationName: job.airportCode || "Airport",
            address: "", lat: null, lng: null, type: "airport", scheduledTime: "",
          });
        }
        stops.push(newStop);
      } else if (job.jobType === "roundtrip") {
        // Airport bookends — insert before the trailing airport.
        newStop.type = "delivery";
        const lastIdx = stops.length - 1;
        if (stops[lastIdx]?.type === "airport") stops.splice(lastIdx, 0, newStop);
        else stops.push(newStop);
      } else {
        stops.push(newStop);
      }
      return { ...f, stops };
    });
  }
  function removeStop(id) {
    setForm((f) => ({
      ...f,
      stops:    (f.stops    || []).filter((s) => s.id !== id),
      packages: (f.packages || []).map((p) => p.stopId === id ? { ...p, stopId: null } : p),
    }));
  }
  function updateStop(id, k, v) {
    setForm((f) => ({
      ...f,
      stops: (f.stops || []).map((s) => s.id === id ? { ...s, [k]: v } : s),
    }));
  }
  // Atomic multi-field update — picking a saved location fills name +
  // address + coords + locationId in one go without stale-closure bugs.
  function patchStop(id, patch) {
    setForm((f) => ({
      ...f,
      stops: (f.stops || []).map((s) => s.id === id ? { ...s, ...patch } : s),
    }));
  }
  function moveStop(idx, dir) {
    const arr = [...(form.stops || [])];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    setField("stops", arr);
  }

  function addPackage(stopId = null) {
    setField("packages", [
      ...(form.packages || []),
      { id: uid(), hawbNumber: "", description: "", declaredValue: "", weight: "", pieces: "", stopId },
    ]);
  }
  function removePackage(id) {
    setField("packages", (form.packages || []).filter((p) => p.id !== id));
  }
  function updatePackage(id, k, v) {
    setField("packages", (form.packages || []).map((p) => p.id === id ? { ...p, [k]: v } : p));
  }
  function reassignPackage(id, stopId) {
    setField("packages", (form.packages || []).map((p) =>
      p.id === id ? { ...p, stopId: stopId || null } : p
    ));
  }

  // ── Reschedule (Case A) ─────────────────────────────────
  async function handleReschedule(newDate, newTime, reason, guardWent, billBroker = true, payGuard = true, noShow = false) {
    // A plain reschedule needs a date; a cancelled trip (guard went) can be
    // billed now with the re-fly date still unknown.
    if (!guardWent && !newDate) return;
    const prevDate = job.scheduledDate || "—";
    const prevTime = job.scheduledTime || "—";
    const why = (reason || "").trim();
    const moveLabel = newDate
      ? `${prevDate}${prevTime !== "—" ? " " + prevTime : ""} → ${newDate}${newTime ? " " + newTime : ""}`
      : `${prevDate}${prevTime !== "—" ? " " + prevTime : ""} → new date TBD`;

    // Plain reschedule (guard didn't go): just move the job to the new date.
    if (!guardWent) {
      await updateJob(jobId, {
        scheduledDate: newDate,
        ...(newTime ? { scheduledTime: newTime } : {}),
      });
      await logAudit({
        jobId, companyId,
        action: `Rescheduled from ${moveLabel}${why ? ` — ${why}` : ""}`,
        userId: userProfile.id, userName: userProfile.name,
      });
      setShowReschedule(false);
      return;
    }

    // Guard went, then it cancelled: bill the original day as the cancelled
    // trip (4h min — owner adjusts in Ready to Bill). NO re-fly job is created
    // — if it flies again the dispatcher issues a new dispatch (new number),
    // and the courier doesn't carry packages forward, so nothing is left
    // hanging if it never re-flies.
    // Same billing either way; only the wording differs (no show = the
    // guard went but nobody was there / the shipment wasn't ready).
    const what = noShow ? "No show" : "Flight cancelled";
    const cancelledStops = (job.stops || []).map((s) =>
      s.type === "airport" ? s : {
        ...s, noShow: true,
        noShowReason: `${what}${why ? ` — ${why}` : ""}`,
        noShowAt: new Date().toISOString(),
      }
    );
    // billBroker / payGuard are independent: if either is on, keep it as a
    // billable/payable record (status complete → shows in Ready to Bill, where
    // the per-guard invoices carry both flags); if neither, just cancel it.
    const keepRecord = billBroker || payGuard;
    await updateJob(jobId, keepRecord
      ? {
          status: "complete",
          completedAt: new Date().toISOString(),
          tripCancelled: true,
          tripCancelledReason: noShow ? `No show${why ? ` — ${why}` : ""}` : why || "Flight cancelled",
          ...(noShow ? { tripNoShow: true } : {}),
          billBroker, payGuard,
          stops: cancelledStops,
        }
      : {
          status: "cancelled",
          tripCancelled: true,
          tripCancelledReason: noShow ? `No show${why ? ` — ${why}` : ""}` : why || "Flight cancelled",
          ...(noShow ? { tripNoShow: true } : {}),
          stops: cancelledStops,
        });
    const billPayLabel = keepRecord
      ? `${billBroker ? "billed broker" : "not billed"}, ${payGuard ? "guard paid" : "guard not paid"} (min)`
      : "not billed, guard not paid";
    await logAudit({
      jobId, companyId,
      action: `${what}${why ? ` — ${why}` : ""} · guard went · ${billPayLabel}`,
      userId: userProfile.id, userName: userProfile.name,
    });
    setShowReschedule(false);
  }

  // ── Move packages to a new job (Case B) ─────────────────
  // packageIds: array of package.id strings to move
  // newDate/newTime: schedule for the cloned job
  async function handleMovePackages(packageIds, newDate, newTime) {
    if (!packageIds?.length || !newDate) return;
    const movedSet = new Set(packageIds);
    const allPkgs = job.packages || [];
    const moved  = allPkgs.filter((p) => movedSet.has(p.id));
    const kept   = allPkgs.filter((p) => !movedSet.has(p.id));

    // Held pieces are already in hand (vaulted) — the carry-over is a vault
    // DELIVERY, not a reschedule: skip the airport (no re-fly) and bill from
    // home. A plain reschedule keeps the airport so the pieces can re-fly on
    // the new date.
    const heldMoved = moved.filter((p) => p.held);
    const isVault = heldMoved.length > 0;
    // Count from this job's date too, not just heldAt — Hold is usually
    // clicked during billing, days after the run.
    const earliestHeld = heldMoved.map((p) => p.heldAt).filter(Boolean).sort()[0] || null;
    const vaultNights = isVault ? vaultNightsBetween([earliestHeld, job.scheduledDate], newDate) : 0;
    // The $100/night vault fee bills on THIS job — the one that vaulted the
    // pieces — never the clone that delivers them (owner confirmed
    // 2026-08-27). The clone bills only the route it drives. Never stack a
    // second fee if one is already here.
    const ownItems = Array.isArray(job.expenseItems) ? job.expenseItems : [];
    const addVaultFee = isVault && !ownItems.some((e) => /vault/i.test(e.label || ""));
    const ownItemsWithFee = addVaultFee
      ? [
          ...(ownItems.length === 0 && Number(job.expenses) > 0
            ? [{ id: `exp_legacy_${Date.now()}`, label: "Expenses", amount: Number(job.expenses), roundTrip: false, receiptPhotoUrl: "" }]
            : []),
          ...ownItems,
          { id: `exp_vault_${Date.now()}`, label: `Vault fee (${vaultNights} night${vaultNights !== 1 ? "s" : ""})`, amount: 100 * vaultNights, roundTrip: false, receiptPhotoUrl: "" },
        ]
      : null;

    // Clone the stops the moved packages need. Skip the airport for a vault
    // delivery; keep it for a reschedule (pieces re-fly).
    const stopIdsNeeded = new Set(moved.map((p) => p.stopId).filter(Boolean));
    const clonedStops = (job.stops || [])
      .filter((s) => stopIdsNeeded.has(s.id) || (s.type === "airport" && !isVault))
      .map((s) => ({ ...s, completedAt: null, completedBy: null, photos: {}, notes: "" }));

    // Strip per-stop execution state; clear the held flag on carried pieces
    // (they're active on the new job now) but stamp the vault provenance.
    const cleanMoved = moved.map((p) => {
      const { held, heldAt, heldBy, heldReason, heldDeliveryDate, heldDeliveryDirection, heldFromJobId, heldFromJobNumber, ...rest } = p;
      const base = { ...rest, photos: {} };
      if (!held) return base;
      return {
        ...base,
        wasVaulted: true,
        vaultedNights: vaultNightsBetween([heldAt, job.scheduledDate], newDate),
        vaultedFromJobId: heldFromJobId || job.id,
        vaultedFromJobNumber: heldFromJobNumber || job.jobNumber || "",
      };
    });

    // Suffix: "-V" for a vault delivery, "-R" for a reschedule. Blank
    // source numbers (some Ferrari/DAI jobs) fall back to the first
    // package's HAWB then broker name so the clone never lands as a
    // bare "-V" (the "grabbed ." banner bug's root cause).
    const numberBase = job.jobNumber || job.packages?.[0]?.hawbNumber || job.brokerName || "JOB";
    const newJobNumber = `${numberBase}-${isVault ? "V" : "R"}`;

    const newDocRef = await createJob({
      companyId:      job.companyId,
      jobNumber:      newJobNumber,
      subJobLabel:    isVault ? "Vault delivery" : "Rescheduled",
      parentJobId:    job.id,
      jobType:        job.jobType,
      airportCode:    job.airportCode || "",
      brokerName:     job.brokerName  || "",
      brokerId:       job.brokerId    || null,
      dispatcherName: job.dispatcherName || "",
      dispatcherId:   job.dispatcherId   || null,
      numGuards:      job.numGuards || 1,
      scheduledDate:  newDate,
      scheduledTime:  newTime || "",
      // Vault delivery: you already hold the pieces, so bill from home. This
      // clone bills only the route it drives — the vault fee stays on the
      // job that did the vaulting.
      billFromHome:   isVault,
      notes:          isVault ? `Vaulted pieces from ${job.jobNumber}` : (job.notes || ""),
      // Carry the owner notes onto the cloned job — billing reminders
      // typically apply to the new dispatch the same way as the original.
      ownerNotes:     job.ownerNotes || "",
      stops:          clonedStops,
      packages:       cleanMoved,
      status:         "intake",
      guardAssignments: [],
      guardIds:       [],
    });

    // Update source job: remove the moved packages, and take the vault fee.
    await updateJob(jobId, {
      packages: kept,
      ...(ownItemsWithFee ? {
        expenseItems: ownItemsWithFee,
        // Round-trip items count double, matching the review panel's total.
        expenses: ownItemsWithFee.reduce((sum, it) => sum + (Number(it.amount) || 0) * (it.roundTrip ? 2 : 1), 0),
      } : {}),
    });

    await logAudit({
      jobId, companyId,
      action: `Moved ${cleanMoved.length} package${cleanMoved.length !== 1 ? "s" : ""} to new job ${newJobNumber} (${newDate})`,
      userId: userProfile.id, userName: userProfile.name,
    });
    await logAudit({
      jobId: newDocRef.id, companyId,
      action: `Created from reschedule of ${job.jobNumber}`,
      userId: userProfile.id, userName: userProfile.name,
    });

    setShowMovePkgs(false);

    // If everything was moved, prompt to cancel the now-empty source.
    if (kept.length === 0) {
      setTimeout(() => {
        if (window.confirm("All packages were moved. Cancel this now-empty job?")) {
          handleCancel();
        }
      }, 150);
    }
  }

  // Split a multi-guard job whose guards ran SEPARATE routes into two
  // single-guard jobs: {jobNumber}-A (this one, trimmed to A's stops) and a new
  // {jobNumber}-B with the rest. The airport stays on both (each guard picked up
  // there). Each then bills its own time + mileage. assignment: { stopId: "A"|"B" }.
  async function handleSplitByGuard(assignment) {
    const allStops = job.stops || [];
    const allPkgs = job.packages || [];
    const isAirport = (s) => s.type === "airport";
    const aStops = allStops.filter((s) => isAirport(s) || assignment[s.id] === "A");
    const bStops = allStops.filter((s) => isAirport(s) || assignment[s.id] === "B");
    const bDelivIds = new Set(bStops.filter((s) => !isAirport(s)).map((s) => s.id));
    const bPkgs = allPkgs.filter((p) => bDelivIds.has(p.stopId));
    const aPkgs = allPkgs.filter((p) => !bDelivIds.has(p.stopId)); // A's + any unassigned
    const ga = job.guardAssignments || [];
    const guardA = ga[0] ? [{ ...ga[0], guardNumber: 1 }] : [];
    const guardB = ga[1] ? [{ ...ga[1], guardNumber: 1 }] : [];
    const base = job.jobNumber || "JOB";

    const newDocRef = await createJob({
      companyId:      job.companyId,
      jobNumber:      `${base}-B`,
      subJobLabel:    "Guard B",
      parentJobId:    job.id,
      jobType:        job.jobType,
      airportCode:    job.airportCode || "",
      brokerName:     job.brokerName || "",
      brokerId:       job.brokerId || null,
      dispatcherName: job.dispatcherName || "",
      dispatcherId:   job.dispatcherId || null,
      numGuards:      1,
      scheduledDate:  job.scheduledDate || "",
      scheduledTime:  job.scheduledTime || "",
      billFromHome:   !!job.billFromHome,
      stops:          bStops,
      packages:       bPkgs,
      guardAssignments: guardB,
      guardIds:       guardB.map((g) => g.guardId).filter(Boolean),
      status:         job.status || "intake",
      notes:          job.notes || "",
      ownerNotes:     job.ownerNotes || "",
    });

    await updateJob(jobId, {
      jobNumber:        `${base}-A`,
      subJobLabel:      "Guard A",
      numGuards:        1,
      stops:            aStops,
      packages:         aPkgs,
      guardAssignments: guardA,
      guardIds:         guardA.map((g) => g.guardId).filter(Boolean),
    });

    await logAudit({
      jobId, companyId,
      action: `Split by guard → ${base}-A (this) + ${base}-B (${bDelivIds.size} stop${bDelivIds.size !== 1 ? "s" : ""})`,
      userId: userProfile.id, userName: userProfile.name,
    });
    await logAudit({
      jobId: newDocRef.id, companyId,
      action: `Created from guard-split of ${base}`,
      userId: userProfile.id, userName: userProfile.name,
    });
    setShowSplit(false);
  }

  // Undo a split: merge {base}-A and {base}-B back into one 2-guard {base} job.
  // Keeps the -A doc (combining in -B's non-airport stops + packages + guard),
  // then deletes -B. The shared airport is deduped by stop id.
  async function handleMergeSplit() {
    const m = /^(.*)-([AB])$/.exec(job.jobNumber || "");
    if (!m) return;
    const base = m[1];
    if (!window.confirm(`Merge ${base}-A and ${base}-B back into one job (${base})? Both guards' stops combine and ${base}-B is deleted.`)) return;
    const snap = await getDocs(query(collection(db, "jobs"), where("companyId", "==", companyId)));
    const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const aJob = all.find((j) => j.jobNumber === `${base}-A`);
    const bJob = all.find((j) => j.jobNumber === `${base}-B`);
    if (!aJob || !bJob) { alert(`Couldn't find both ${base}-A and ${base}-B to merge.`); return; }

    const aStopIds = new Set((aJob.stops || []).map((s) => s.id));
    const mergedStops = [...(aJob.stops || []), ...(bJob.stops || []).filter((s) => !aStopIds.has(s.id))];
    const aPkgIds = new Set((aJob.packages || []).map((p) => p.id));
    const mergedPkgs = [...(aJob.packages || []), ...(bJob.packages || []).filter((p) => !aPkgIds.has(p.id))];
    const ga = [];
    if (aJob.guardAssignments?.[0]) ga.push({ ...aJob.guardAssignments[0], guardNumber: 1 });
    if (bJob.guardAssignments?.[0]) ga.push({ ...bJob.guardAssignments[0], guardNumber: 2 });

    await updateJob(aJob.id, {
      jobNumber:        base,
      subJobLabel:      "",
      numGuards:        2,
      stops:            mergedStops,
      packages:         mergedPkgs,
      guardAssignments: ga,
      guardIds:         ga.map((g) => g.guardId).filter(Boolean),
    });
    await deleteJobHard(bJob.id, companyId);
    await logAudit({
      jobId: aJob.id, companyId,
      action: `Merged ${base}-A + ${base}-B back into ${base}`,
      userId: userProfile.id, userName: userProfile.name,
    });
    // If we were viewing the now-deleted -B, close the modal.
    if (jobId === bJob.id) onClose();
  }

  if (!job) {
    if (!resolved) {
      return (
        <Overlay onClose={onClose}>
          <div className="loading-state" style={{ padding: 60 }}>Loading job...</div>
        </Overlay>
      );
    }
    // Listener resolved but the job doc doesn't exist. Most common cause:
    // the invoice references a job created by an older Forward-bundle
    // flow that didn't write a Job doc (Ferrari / DAI early invoices).
    return (
      <Overlay onClose={onClose}>
        <div style={{
          padding: 40, textAlign: "center", color: "#444",
          display: "flex", flexDirection: "column", gap: 12, alignItems: "center",
        }}>
          <div style={{ fontSize: 36 }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Job record not found</div>
          <div style={{ fontSize: 12, color: "#888", maxWidth: 360, lineHeight: 1.5 }}>
            This invoice references a job that doesn't have a corresponding
            record in the database. The invoice is still valid — open the
            Invoices page to view, mark sent, or mark paid. If this is a
            Forward-bundle invoice (Ferrari / DAI), the underlying job was
            never written as a separate doc.
          </div>
          <div style={{ fontSize: 10, color: "#aaa", fontFamily: "monospace" }}>
            job id: {jobId}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#185FA5", color: "#fff", border: "none",
              borderRadius: 6, padding: "8px 16px", fontSize: 12,
              fontWeight: 700, cursor: "pointer",
            }}
          >Close</button>
        </div>
      </Overlay>
    );
  }

  const st       = STATUS_LABELS[job.status] || { label: job.status, chip: "chip-gray" };
  // Edit window: open until the job is either cancelled or the invoice has
  // been approved. A `complete` job that's still in Ready-to-Bill (or even
  // sitting as a draft invoice) stays editable so the owner can correct a
  // wrong stop, missed location, broker mix-up, etc. Once invoiceStatus is
  // "invoiced" or beyond, the doc locks to preserve the billing audit trail.
  const isInvoiceLocked =
    job.invoiceStatus === "invoiced" ||
    job.invoiceStatus === "sent" ||
    job.invoiceStatus === "paid";
  const canEdit = job.status !== "cancelled" && !isInvoiceLocked;
  const isMalca  = job.brokerName?.toLowerCase().includes("malca");
  // Broker-specific name for hawbNumber: Receipt No. (Malca) / Order No. (Ferrari)
  const pkgLabel = hawbLabel(job);
  const pkgNumLabel = hawbNumLabel(job);

  function fmt(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function fmtLog(ts) {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  }

  const displayAssignments = editing ? form.guardAssignments : (job.guardAssignments || []);

  return (
    <Overlay onClose={onClose}>
      {/* Header */}
      <div style={{
        padding: "14px 20px", borderBottom: "1px solid #e8e8e8",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#185FA5" }}>{jobDisplayNumber(job)}</span>
          {job.subJobLabel && <span style={{ fontSize: 12, color: "#888" }}>({job.subJobLabel})</span>}
          <span className={`chip ${st.chip}`}>{st.label}</span>
          {/* On phones type / broker / email source move into the summary card. */}
          {!isPhone && <span className="chip chip-gray">{JOB_TYPE_LABELS[job.jobType] || job.jobType}</span>}
          {!isPhone && isMalca && <span className="chip chip-purple">Malca</span>}
          {!isPhone && job.intake?.source === "email" && (
            <span
              className="chip chip-teal"
              title={`Created or linked by Ferrari email intake · order ${job.intake.orderNo || ""} · ${(job.intake.emailIds || []).length} email(s)`}
            >
              📨 From email{job.intake.state === "tentative" ? " · tentative" : ""}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {canEdit && !editing && (
            <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>
          )}
          {editing && <>
            <button className="btn btn-sm" onClick={handleDiscard}>Discard</button>
            <button className="btn btn-sm btn-blue" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>}
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", padding: "2px 6px", lineHeight: 1 }}>
            ✕
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", padding: "0 20px", borderBottom: "1px solid #e8e8e8", flexShrink: 0 }}>
        {[
          { id: "details", label: "Details" },
          { id: "guards",  label: "Guards" },
          { id: "log",     label: "Audit log" },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)} style={{
            background: "none", border: "none", padding: "10px 14px", fontSize: 12,
            fontWeight: tab === id ? 700 : 500,
            color: tab === id ? "#185FA5" : "#888",
            borderBottom: tab === id ? "2px solid #185FA5" : "2px solid transparent",
            cursor: "pointer", marginBottom: -1,
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>

        {error && (
          <div style={{ background: "#fff0f0", border: "1px solid #f5c5c5", borderRadius: 7, padding: "8px 12px", fontSize: 11, color: "#a32d2d", marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* Banner when editing a job that's already completed but not yet
            invoiced. Reminds the owner the billing snapshot was taken at
            completion — fixes here need a re-Review to flow through. */}
        {job.status === "complete" && !isInvoiceLocked && editing && (
          <div style={{
            background: "#fff7e0", border: "1px solid #f0c878", borderRadius: 7,
            padding: "10px 12px", fontSize: 11, color: "#7a5400", marginBottom: 14,
          }}>
            ✏️ This job is already complete. Edits to stops, mileage, or rates
            won't update the billing automatically — open Ready to Bill and
            re-run Review to recompute the total before approving.
          </div>
        )}
        {isInvoiceLocked && (
          <div style={{
            background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 7,
            padding: "10px 12px", fontSize: 11, color: "#666", marginBottom: 14,
          }}>
            🔒 This job is locked because the invoice has been {job.invoiceStatus === "invoiced" ? "approved" : job.invoiceStatus}. Void the invoice to edit again.
          </div>
        )}

        {/* ══ DETAILS ══ */}
        {tab === "details" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Job info — every field becomes an input when `editing` is on.
                Phones (view mode) get a short summary card instead. */}
            {isPhone && !editing ? (() => {
              const required = Number(job.numGuards) || (job.guardAssignments?.length || 1);
              const assigned = (job.guardAssignments || []).filter((g) => g.guardId || g.guardName);
              const firstTime = job.jobType === "direct"
                ? job.scheduledTime
                : (job.stops || []).find((s) => s.type !== "airport" && s.scheduledTime)?.scheduledTime
                  || (job.stops || []).find((s) => s.scheduledTime)?.scheduledTime;
              const dateLabel = job.scheduledDate
                ? (() => {
                    const [y, m, d] = job.scheduledDate.split("-").map(Number);
                    return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: y === new Date().getFullYear() ? undefined : "numeric" });
                  })()
                : "Not scheduled";
              const shortBroker = String(job.brokerName || "").replace(/,?\s+(express\s+)?(inc\.?|llc|corp\.?)$/i, "").trim();
              return (
                <div className="panel" style={{ padding: "12px 14px" }}>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {JOB_TYPE_LABELS[job.jobType] || job.jobType || "Job"}{shortBroker ? ` · ${shortBroker}` : ""}
                  </div>
                  <div style={{ fontSize: 13, color: "#5b6675", marginTop: 3 }}>
                    {[
                      shortBroker !== job.brokerName && job.brokerName,
                      job.dispatcherName && !String(job.brokerName || "").toLowerCase().startsWith(String(job.dispatcherName).toLowerCase()) && job.dispatcherName,
                      job.jobType !== "direct" && job.airportCode,
                    ].filter(Boolean).join(" · ")}
                  </div>
                  {job.salesRepName && (
                    <div style={{ fontSize: 13, color: "#333", marginTop: 3 }}>
                      👤 {job.salesRepName}
                      {job.salesRepPhone && <> · <a href={telHref(job.salesRepPhone)} style={{ color: "#185FA5" }}>📞 {formatPhone(job.salesRepPhone)}</a></>}
                    </div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}>
                    {dateLabel}{firstTime ? ` · ${firstTime}` : ""}
                  </div>
                  {job.intake?.source === "email" && (
                    <div style={{ fontSize: 12, color: "#0f6e6e", marginTop: 4 }}>
                      📨 From email{job.intake.state === "tentative" ? " · tentative" : ""}
                    </div>
                  )}
                  <div onClick={() => setTab("guards")} style={{
                    marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: assigned.length >= required ? "#eef8f1" : "#fff7e6",
                    border: `1px solid ${assigned.length >= required ? "#bfe3cb" : "#f3d9a4"}`,
                    borderRadius: 10, padding: "9px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                    color: assigned.length >= required ? "#0a7a4a" : "#8a5a00",
                  }}>
                    <span>
                      {assigned.length}/{required} guard{required !== 1 ? "s" : ""} assigned
                      {assigned.length > 0 && ` · ${assigned.map((g) => g.guardName).filter(Boolean).join(", ")}`}
                    </span>
                    <b style={{ color: "#185FA5" }}>{assigned.length >= required ? "Guards ›" : "Assign ›"}</b>
                  </div>
                  <div style={{ fontSize: 11, color: "#999", marginTop: 8 }}>Created {fmt(job.createdAt)}</div>
                </div>
              );
            })() : (
            <div className="panel">
              <div className="panel-header"><div className="panel-title">Job info</div></div>
              {/* Compact 2-up grid — halves the scroll vs. one field per
                  row. Rows with wide controls span both columns. */}
              <div className="field-grid">
              <Row label="Job #">
                {editing
                  ? <input value={form.jobNumber}
                      onChange={(e) => setField("jobNumber", e.target.value)}
                      placeholder={(form.brokerName || "").toLowerCase().includes("malca") ? "e.g. MAKY26-1901" : "Optional"}
                      style={{ flex: 1 }} />
                  : (job.jobNumber || <span style={{ color: "#bbb" }}>—</span>)}
              </Row>
              <Row label="Type" full={editing}>
                {editing ? (
                  <div style={{ display: "flex", gap: 4, flex: 1 }}>
                    {["inbound","outbound","roundtrip","direct"].map((t) => (
                      <button key={t} type="button"
                        onClick={() => setField("jobType", t)}
                        className={`btn btn-sm ${form.jobType === t ? "btn-blue" : ""}`}
                        style={{ flex: 1 }}>
                        {t === "roundtrip" ? "Round trip" : t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                ) : (JOB_TYPE_LABELS[job.jobType] || job.jobType || "—")}
              </Row>
              <Row label="Broker">
                {editing ? (
                  <select value={form.brokerId}
                    onChange={(e) => {
                      const b = brokers.find((x) => x.id === e.target.value);
                      setField("brokerId", e.target.value);
                      setField("brokerName", b?.name || "");
                      // Also pre-fill dispatcher if broker has a default
                      if (b?.dispatcherName && !form.dispatcherName) setField("dispatcherName", b.dispatcherName);
                    }}
                    style={{ flex: 1 }}>
                    <option value="">— pick broker —</option>
                    {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                ) : (job.brokerName || "—")}
              </Row>
              <Row label="Dispatcher">
                {editing
                  ? <input value={form.dispatcherName}
                      onChange={(e) => setField("dispatcherName", e.target.value)}
                      style={{ flex: 1 }} />
                  : (job.dispatcherName || "—")}
              </Row>
              {/* Sales rep fields shown only for direct jobs (the only kind
                  where the guard meets a rep). Hide for inbound/outbound. */}
              {(form.jobType === "direct" || job.salesRepName || job.salesRepPhone) && (
                <>
                  <Row label="Sales rep">
                    {editing
                      ? <SalesRepAutocomplete
                          value={form.salesRepName}
                          brokerId={form.brokerId}
                          knownReps={knownReps}
                          onPick={(rep) => {
                            // Picking a suggestion swaps the rep wholesale —
                            // name AND phone. The previous phone belonged
                            // to the previous rep, so keeping it around
                            // would be wrong. If the picked rep has no
                            // phone on file, fall back to whatever was in
                            // the field (or blank) so we don't erase a
                            // number the owner just typed.
                            setForm((f) => ({
                              ...f,
                              salesRepName: rep.name,
                              salesRepPhone: rep.phone || f.salesRepPhone || "",
                            }));
                          }}
                          onChange={(v) => setField("salesRepName", v)}
                        />
                      : (job.salesRepName || <span style={{ color: "#bbb" }}>—</span>)}
                  </Row>
                  <Row label="Rep phone">
                    {editing
                      ? <PhoneInput value={form.salesRepPhone}
                          onChange={(v) => setField("salesRepPhone", v)}
                          style={{ flex: 1 }} />
                      : (job.salesRepPhone
                          ? <a href={telHref(job.salesRepPhone) || "#"}
                              style={{ color: "#185FA5", textDecoration: "none" }}>
                              {formatPhone(job.salesRepPhone)}
                            </a>
                          : <span style={{ color: "#bbb" }}>—</span>)}
                  </Row>

                  {/* Bundle status row — always visible while editing so
                      the owner knows up front whether this job is part of
                      a bundle (and if not, why the bulk-apply option is
                      missing). Three possible states:
                        1. Has bundle + editable siblings → show checkbox
                        2. Has bundle but every sibling is invoiced → info
                        3. No bundle at all → info, suggesting how to bundle */}
                  {editing && (
                    <Row label="" full>
                      {job.bundleId && editableSiblings.length > 0 ? (
                        <label style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: "#fffbe9", border: "1px solid #f0d878",
                          borderRadius: 7, padding: "8px 10px",
                          fontSize: 12, cursor: "pointer", flex: 1,
                        }}>
                          <input
                            type="checkbox"
                            checked={applyRepToBundle}
                            onChange={(e) => setApplyRepToBundle(e.target.checked)}
                          />
                          <span>
                            📎 Apply this rep to all{" "}
                            <strong>{editableSiblings.length + 1}</strong> jobs
                            in this bundle (this one + {editableSiblings.length} other
                            {editableSiblings.length === 1 ? "" : "s"})
                            {bundleSiblings.length !== editableSiblings.length && (
                              <span style={{ color: "#888", fontWeight: 400 }}>
                                {" "}({bundleSiblings.length - editableSiblings.length} already invoiced — won't change)
                              </span>
                            )}
                          </span>
                        </label>
                      ) : job.bundleId ? (
                        <div style={{
                          flex: 1, fontSize: 11, color: "#888",
                          background: "#f8f8f8", border: "1px solid #eee",
                          borderRadius: 7, padding: "8px 10px",
                        }}>
                          📎 Part of a bundle, but the other {bundleSiblings.length} sibling
                          {bundleSiblings.length === 1 ? "" : "s"} are already invoiced — bulk apply unavailable.
                        </div>
                      ) : (
                        <div style={{
                          flex: 1, fontSize: 11, color: "#888",
                          background: "#f8f8f8", border: "1px solid #eee",
                          borderRadius: 7, padding: "8px 10px",
                        }}>
                          Not part of a bundle — this is a standalone job. Multi-detail
                          DAI emails create bundled jobs (look for the 📎 chip in the inbox).
                        </div>
                      )}
                    </Row>
                  )}
                </>
              )}
              {(form.jobType !== "direct") && (
                <Row label="Airport">
                  {editing
                    ? <input value={form.airportCode}
                        onChange={(e) => setField("airportCode", e.target.value.toUpperCase())}
                        maxLength={3} placeholder="CLT"
                        style={{ width: 80 }} />
                    : (job.airportCode || "—")}
                </Row>
              )}
              <Row label="Date">
                {editing
                  ? <div style={{ flex: 1 }}>
                      <input type="date" value={form.scheduledDate}
                        onChange={(e) => setField("scheduledDate", e.target.value)}
                        style={{ width: "100%" }} />
                      {/* Year-typo guard: a year far from today files the job
                          (and its invoice) under the wrong billing period. */}
                      {(() => {
                        const y = Number(String(form.scheduledDate || "").slice(0, 4));
                        const now = new Date().getFullYear();
                        if (!y || (y >= now - 1 && y <= now + 1)) return null;
                        return (
                          <div style={{ fontSize: 10, color: "#a36b1d", fontWeight: 600, marginTop: 2 }}>
                            ⚠ Year {y} — is that right? A wrong year files the job under the wrong billing month.
                          </div>
                        );
                      })()}
                    </div>
                  : (job.scheduledDate || "—")}
              </Row>
              {/* Job-level time only meaningful for direct jobs. Inbound/outbound
                  use per-stop times (each pickup has its own scheduledTime). */}
              {(form.jobType === "direct" || (job.jobType === "direct" && !editing)) && (
                <Row label="Time">
                  {editing
                    ? <input type="time" value={form.scheduledTime}
                        onChange={(e) => setField("scheduledTime", e.target.value)}
                        style={{ flex: 1 }} />
                    : (job.scheduledTime || "—")}
                </Row>
              )}
              {/* (Clock start/end fields removed — billing pickup time now
                  derives from the airport stop's scheduledTime + the company's
                  default arrival time, set in the Review modal.) */}
              <Row label="Created">{fmt(job.createdAt)}</Row>
              </div>
            </div>
            )}

            {jobInvoices.length > 0 && (
              <div className="panel">
                <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div className="panel-title">🧾 Invoice</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-blue"
                    onClick={() => {
                      const q = job.jobNumber || jobInvoices[0].invoiceNumber || "";
                      onClose();
                      navigate(`/owner/invoices?q=${encodeURIComponent(q)}`);
                    }}
                  >
                    Open in Invoices →
                  </button>
                </div>
                {jobInvoices.map((inv) => {
                  const st = INVOICE_STATUS_STYLE[inv.status] || INVOICE_STATUS_STYLE.draft;
                  return (
                    <div key={inv.id} style={{
                      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                      padding: "10px 16px", borderTop: "1px solid #f0f0f0", fontSize: 13,
                    }}>
                      <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#185FA5" }}>{inv.invoiceNumber}</span>
                      <span style={{ color: "#888" }}>G{inv.guardNumber}{inv.guardName ? ` · ${inv.guardName}` : ""}</span>
                      <span style={{ color: "#555" }}>{Number(inv.hoursCharged) || 0}h · {Number(inv.miles) || 0} mi</span>
                      <span style={{ fontWeight: 700 }}>${(Number(inv.total) || 0).toFixed(2)}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                        color: st.color, background: st.bg, textTransform: "capitalize",
                      }}>{inv.status || "draft"}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <BillingBreakdown job={job} />

            {/* Notes + Owner notes side by side on desktop (stacked on
                phones) — two short panels don't each need a full row. */}
            <div className="panel-2up">
            {/* Carrying vaulted pieces. Derived from the packages themselves
                rather than a note written at move time, so it also shows on
                jobs that took a piece before that note existed, and it can't
                go stale if the piece moves on again. */}
            {(() => {
              const carried = (job.packages || []).filter((p) => p?.wasVaulted);
              if (!carried.length) return null;
              return (
                <div className="panel" style={{ background: "#fffdf5" }}>
                  <div className="panel-header" style={{ background: "#fffdf5" }}>
                    <div className="panel-title">
                      🔒 Carrying vaulted {carried.length === 1 ? "piece" : "pieces"}
                    </div>
                    <span style={{ fontSize: 10, color: "#a36b1d", fontWeight: 600 }}>
                      already in hand — not arriving on a flight
                    </span>
                  </div>
                  <div style={{ padding: "10px 14px", fontSize: 12, lineHeight: 1.65 }}>
                    {carried.map((p, i) => (
                      <div key={p.id || i}>
                        <strong>{p.hawbNumber || "Piece"}</strong>
                        {" from "}
                        <strong>{p.vaultedFromJobNumber || "an earlier job"}</strong>
                        {p.vaultedNights
                          ? ` · ${p.vaultedNights} night${p.vaultedNights !== 1 ? "s" : ""} in the vault`
                          : ""}
                        {" · vault fee bills on that job, not this one"}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Phones: an empty note is a small "+ Add" link (opens Edit). */}
            {isPhone && !editing && !job.notes ? (
              canEdit && <button type="button" onClick={() => setEditing(true)} style={{ background: "none", border: "none", padding: "4px 2px", textAlign: "left", fontSize: 14, fontWeight: 600, color: "#185FA5", cursor: "pointer" }}>+ Add note</button>
            ) : (
            /* Notes (visible to guards). */
            <div className="panel">
              <div className="panel-header">
                <div className="panel-title">Notes</div>
                <span style={{ fontSize: 10, color: "#888" }}>visible to guards</span>
              </div>
              <div style={{ padding: "10px 14px" }}>
                {editing
                  ? <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
                      style={{ width: "100%", minHeight: 72, fontSize: 12 }} />
                  : <div style={{ fontSize: 12, color: job.notes ? "#111" : "#bbb", lineHeight: 1.65 }}>
                      {job.notes || "No notes"}
                    </div>
                }
              </div>
            </div>
            )}

            {/* Owner-only notes — never sent to guards. Yellow tint so the
                visibility distinction is obvious whether you're scanning,
                editing, or just reviewing. */}
            {isPhone && !editing && !job.ownerNotes ? (
              canEdit && <button type="button" onClick={() => setEditing(true)} style={{ background: "none", border: "none", padding: "4px 2px", textAlign: "left", fontSize: 14, fontWeight: 600, color: "#185FA5", cursor: "pointer" }}>+ Add owner note (private)</button>
            ) : (
            <div className="panel" style={{ background: "#fffbe9" }}>
              <div className="panel-header" style={{ background: "#fffbe9" }}>
                <div className="panel-title">🔒 Owner notes</div>
                <span style={{ fontSize: 10, color: "#a36b1d", fontWeight: 600 }}>private — guards never see this</span>
              </div>
              <div style={{ padding: "10px 14px" }}>
                {editing
                  ? <textarea
                      value={form.ownerNotes || ""}
                      onChange={(e) => setField("ownerNotes", e.target.value)}
                      placeholder="Pricing notes, billing reminders, things to watch..."
                      style={{ width: "100%", minHeight: 72, fontSize: 12, background: "#fff", border: "1px solid #f0d878" }}
                    />
                  : <div style={{ fontSize: 12, color: job.ownerNotes ? "#111" : "#bbb", lineHeight: 1.65 }}>
                      {job.ownerNotes || "No owner notes"}
                    </div>
                }
              </div>
            </div>
            )}
            </div>

            {/* POD photos panel — visible whenever any stop has a captured
                POD photo. Lets the owner email all POD URLs to the broker
                (typical workflow for Ferrari, also handy for Malca). The
                owner taps "Compose email" → their email client opens with
                subject + body pre-filled, and they manually attach the
                actual photos by tapping the View links and saving. We
                can't auto-attach via mailto: (browser limitation), but
                seeding the URLs and subject saves a couple minutes of
                copy-paste per job. */}
            {(() => {
              // Surface every POD photo on the job, organized by stop.
              // Each row shows the HAWB number as a clickable link that
              // opens the photo in a new tab — the natural workflow
              // when Malca/Ferrari calls and needs the POD for a
              // specific HAWB. Falls back to "Stop N" labels for
              // legacy stops that have a stop-level podPhotoUrl but no
              // per-package linkage.
              const allPackages = job.packages || [];
              const allStops = job.stops || [];
              // Build row records: one per photo (package-level OR
              // legacy stop-level), in stop order. Skip duplicates if a
              // stop has both stop.podPhotoUrl and packages with their
              // own podPhotoUrl (per-package wins).
              const rows = [];
              for (const stop of allStops) {
                if (stop.type === "airport") continue;
                const stopPkgs = allPackages
                  .filter((p) => p.stopId === stop.id && p.podPhotoUrl);
                if (stopPkgs.length > 0) {
                  for (const pkg of stopPkgs) {
                    rows.push({
                      key: pkg.id,
                      hawb: pkg.hawbNumber || "",
                      stopName: stop.locationName || stop.address || "",
                      photoUrl: pkg.podPhotoUrl,
                    });
                  }
                } else if (stop.podPhotoUrl) {
                  // Legacy: stop-level POD, no per-package data.
                  rows.push({
                    key: `stop_${stop.id}`,
                    hawb: "",
                    stopName: stop.locationName || stop.address || "",
                    photoUrl: stop.podPhotoUrl,
                  });
                }
              }
              if (rows.length === 0) return null;

              // Clean filename per photo for the Download buttons.
              const filenameFor = (row, i) => {
                const safe = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "");
                const job_part = safe(job.jobNumber || job.brokerName || "JOB");
                const hawb_part = row.hawb ? safe(row.hawb) : `STOP-${i + 1}`;
                const loc_part = safe(row.stopName).slice(0, 30);
                return `POD_${job_part}_${hawb_part}${loc_part ? `_${loc_part}` : ""}.jpg`;
              };

              // Cross-origin Firebase Storage URLs ignore the plain
              // `download` attribute, so we fetch, blob, and trigger.
              async function downloadOne(row, idx) {
                try {
                  const res = await fetch(row.photoUrl);
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = filenameFor(row, idx);
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (err) {
                  console.error("POD download failed", err);
                  window.open(row.photoUrl, "_blank");
                }
              }
              async function downloadAll() {
                for (let i = 0; i < rows.length; i++) {
                  // eslint-disable-next-line no-await-in-loop
                  await downloadOne(rows[i], i);
                }
              }

              return (
                <div className="panel">
                  <div className="panel-header">
                    <div className="panel-title">📷 POD photos</div>
                    <span style={{ fontSize: 10, color: "#888" }}>
                      {rows.length} photo{rows.length === 1 ? "" : "s"} captured
                    </span>
                  </div>
                  <div style={{ padding: "10px 14px" }}>
                    {/* List view — each row is a HAWB link + thumbnail
                        + per-row download. Click the HAWB or thumbnail
                        to open the photo in a new tab. */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                      {rows.map((row, i) => (
                        <div key={row.key} style={{
                          display: "flex", alignItems: "center", gap: 12,
                          padding: "8px 10px", border: "1px solid #e8e8e8",
                          borderRadius: 7, background: "#fafbfc",
                        }}>
                          <a
                            href={row.photoUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "block",
                              width: 56, height: 56,
                              border: "1px solid #ddd", borderRadius: 5,
                              overflow: "hidden", flexShrink: 0,
                              background: "#fff",
                            }}
                          >
                            <img
                              src={row.photoUrl}
                              alt={row.hawb || `POD ${i + 1}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          </a>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {row.hawb ? (
                              <a
                                href={row.photoUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontSize: 13, fontWeight: 700,
                                  color: "#185FA5", fontFamily: "monospace",
                                  textDecoration: "none",
                                }}
                                title="Open POD photo in new tab"
                              >
                                {row.hawb}
                              </a>
                            ) : (
                              <span style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>
                                No {pkgLabel} on file
                              </span>
                            )}
                            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                              {row.stopName || "—"}
                            </div>
                          </div>
                          <button
                            onClick={() => downloadOne(row, i)}
                            className="btn btn-sm"
                            style={{ flexShrink: 0 }}
                            title={`Download ${filenameFor(row, i)}`}
                          >⬇ Download</button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={downloadAll}
                      className="btn btn-blue"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ fontSize: 14 }}>⬇</span>
                      Download all {rows.length} photo{rows.length === 1 ? "" : "s"}
                    </button>
                    <div style={{ fontSize: 10, color: "#888", marginTop: 6 }}>
                      Click any {pkgLabel} or thumbnail to view the photo. Downloads save as
                      {" "}<code>POD_{job.jobNumber || "JOB"}_HAWB_LOCATION.jpg</code>.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Stops & packages — nested; editable when `editing` is on */}
            {(() => {
              const stopsSrc    = editing ? (form.stops    || []) : (job.stops    || []);
              const packagesSrc = editing ? (form.packages || []) : (job.packages || []);
              if (!editing && stopsSrc.length === 0) return null;
              const orphans = packagesSrc.filter((p) => !p.stopId || !stopsSrc.find((s) => s.id === p.stopId));
              return (
                <div className="panel">
                  <div className="panel-header">
                    <div className="panel-title">Stops &amp; packages</div>
                    <span className="chip chip-gray">
                      {stopsSrc.length} stop{stopsSrc.length !== 1 ? "s" : ""} ·
                      {" "}{packagesSrc.length} pkg{packagesSrc.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {stopsSrc.map((stop, i) => {
                    const stopPkgs = packagesSrc.filter((p) => p.stopId === stop.id);
                    const typeColor = {
                      pickup:   { bg: "#E6F1FB", color: "#185FA5" },
                      delivery: { bg: "#E6F7EC", color: "#2d7a2d" },
                      airport:  { bg: "#f0e8ff", color: "#6b2fb3" },
                    }[stop.type] || { bg: "#f2f3f5", color: "#555" };
                    const isAirport = stop.type === "airport";
                    const canMoveUp   = editing && i > 0                      && !isAirport && stopsSrc[i - 1]?.type !== "airport";
                    const canMoveDown = editing && i < stopsSrc.length - 1    && !isAirport && stopsSrc[i + 1]?.type !== "airport";
                    const phoneView = isPhone && !editing;
                    const mapsHref = stop.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address)}` : null;
                    // Phones: the airport is a slim line, not a full stop card.
                    if (phoneView && isAirport) {
                      const atEnd = i === stopsSrc.length - 1 && i > 0;
                      return (
                        <div key={stop.id || i} style={{ borderBottom: "1px solid #f0f0f0", padding: "9px 14px", fontSize: 13, color: "#6b2fb3", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "baseline" }}>
                          <span style={{ fontWeight: 700 }}>✈ {atEnd ? "Back to " : ""}{stop.locationName || job.airportCode || "Airport"} airport</span>
                          {stop.scheduledTime && <span style={{ fontWeight: 700, color: "#111" }}>· {stop.scheduledTime}</span>}
                          {mapsHref && <a href={mapsHref} target="_blank" rel="noreferrer" style={{ color: "#185FA5", fontSize: 12 }}>📍 {stop.address}</a>}
                        </div>
                      );
                    }
                    const stopNumber = phoneView ? stopsSrc.slice(0, i + 1).filter((s) => s.type !== "airport").length : i + 1;
                    return (
                      <div key={stop.id || i} style={{ borderBottom: "1px solid #f0f0f0", padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <div style={{
                            width: 22, height: 22, borderRadius: "50%",
                            background: "#185FA5", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
                          }}>
                            {stopNumber}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {editing ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {/* Airport stops: one-click backfill from
                                    the company's saved Hub cargo address
                                    (Settings). Saves typing per job and
                                    standardizes the address across all jobs. */}
                                {isAirport && companyDoc?.hubAirportAddress && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      updateStop(stop.id, "address", companyDoc.hubAirportAddress);
                                      if (companyDoc.hubAirportLat != null) {
                                        updateStop(stop.id, "lat", companyDoc.hubAirportLat);
                                        updateStop(stop.id, "lng", companyDoc.hubAirportLng);
                                      }
                                      if (companyDoc.hubAirportNotes && !stop.accessNotes) {
                                        updateStop(stop.id, "accessNotes", companyDoc.hubAirportNotes);
                                      }
                                    }}
                                    style={{
                                      alignSelf: "flex-start",
                                      background: "#eef3fb", border: "1px solid #d6e0ee",
                                      color: "#185FA5", borderRadius: 7,
                                      padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                    }}
                                  >
                                    📍 Use hub address ({companyDoc.hubAirportAddress.length > 40 ? companyDoc.hubAirportAddress.slice(0, 38) + "…" : companyDoc.hubAirportAddress})
                                  </button>
                                )}
                                {/* Out-of-town toggle — direct jobs only.
                                    Big amber callout block so it's impossible
                                    to miss when fixing a stop's destination. */}
                                {(form.jobType || job.jobType) === "direct" && !isAirport && (
                                  <label style={{
                                    display: "flex", alignItems: "center", gap: 8,
                                    background: stop.outOfTown ? "#fff7e0" : "#fafbfc",
                                    border: `1px solid ${stop.outOfTown ? "#f0c878" : "#e0e6f0"}`,
                                    borderRadius: 7, padding: "8px 10px",
                                    fontSize: 12, color: stop.outOfTown ? "#7a5400" : "#444",
                                    cursor: "pointer", fontWeight: stop.outOfTown ? 700 : 500,
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={!!stop.outOfTown}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        updateStop(stop.id, "outOfTown", checked);
                                        if (checked) {
                                          updateStop(stop.id, "locationName", "OUT OF TOWN");
                                          updateStop(stop.id, "address", "");
                                          updateStop(stop.id, "lat", null);
                                          updateStop(stop.id, "lng", null);
                                        } else {
                                          // Clearing OOT — wipe the sentinel so the user can re-pick
                                          // a real location without manually deleting the placeholder.
                                          if (stop.locationName === "OUT OF TOWN") {
                                            updateStop(stop.id, "locationName", "");
                                          }
                                        }
                                      }}
                                      style={{ width: 18, height: 18, flexShrink: 0 }}
                                    />
                                    <span>
                                      🚗 <strong>Out of town</strong>
                                      {" "}— follow rep ~10 min then break, no fixed destination
                                    </span>
                                  </label>
                                )}
                                <LocationNameAutocomplete
                                  value={stop.locationName || ""}
                                  locations={locations}
                                  disabled={!!stop.outOfTown}
                                  onChange={(name) => updateStop(stop.id, "locationName", name)}
                                  onPick={(loc) => {
                                    // Atomic multi-field write so name +
                                    // address + lat/lng + locationId all
                                    // update from one snapshot.
                                    patchStop(stop.id, {
                                      locationName: loc.name,
                                      address: loc.address || "",
                                      lat: typeof loc.lat === "number" ? loc.lat : null,
                                      lng: typeof loc.lng === "number" ? loc.lng : null,
                                      locationId: loc.id || null,
                                    });
                                  }}
                                />
                                <div style={{ position: "relative" }}>
                                  {stop.outOfTown ? (
                                    <input
                                      value="(no fixed destination)"
                                      disabled
                                      style={{ fontSize: 11, width: "100%", background: "#f0f0f0", color: "#888", fontStyle: "italic" }}
                                    />
                                  ) : (
                                    <PlacesAutocomplete
                                      value={stop.address || ""}
                                      onChange={(addr, lat, lng) => {
                                        updateStop(stop.id, "address", addr);
                                        if (lat != null) {
                                          updateStop(stop.id, "lat", lat);
                                          updateStop(stop.id, "lng", lng);
                                        }
                                      }}
                                      placeholder="Address"
                                      style={{ fontSize: 11, color: "#555", width: "100%", paddingRight: stop.lat ? 22 : undefined }}
                                    />
                                  )}
                                  {stop.lat && (
                                    <span style={{
                                      position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                                      fontSize: 10, color: "#2d7a2d", pointerEvents: "none",
                                    }}>✓</span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>
                                  {stop.outOfTown ? (
                                    <span style={{
                                      background: "#fff7e0", color: "#a36b1d",
                                      border: "1px solid #f0c878", borderRadius: 10,
                                      padding: "2px 10px", fontSize: 11, fontWeight: 700,
                                    }}>🚗 OUT OF TOWN</span>
                                  ) : (
                                    stop.locationName || "—"
                                  )}
                                </div>
                                {!stop.outOfTown && stop.address && (phoneView && mapsHref
                                  ? <a href={mapsHref} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 13, color: "#185FA5", marginTop: 3 }}>📍 {stop.address}</a>
                                  : <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{stop.address}</div>
                                )}
                                {stop.outOfTown && (
                                  <div style={{ fontSize: 10, color: "#7a5400", marginTop: 2 }}>
                                    Follow rep ~{job.ootFollowMinutes ? `${job.ootFollowMinutes} min` : job.ootFollowMiles ? `${job.ootFollowMiles} mi` : "broker rule"} then break
                                  </div>
                                )}
                                {/* Contacts on file at this stop's saved
                                    location. Read-only. Tap to call /
                                    email — useful for the owner to ring
                                    a recipient before dispatching the
                                    guard. Only renders when the stop has
                                    a known locationId AND that location
                                    has at least one contact. */}
                                {(() => {
                                  if (!stop.locationId) return null;
                                  const loc = locationsById[stop.locationId];
                                  if (!loc || !Array.isArray(loc.contacts)) return null;
                                  const contacts = loc.contacts.filter((c) => c.name || c.phone || c.email);
                                  if (contacts.length === 0) return null;
                                  return (
                                    <div style={{
                                      marginTop: 6, padding: "6px 8px",
                                      background: "#eef4fa", border: "1px solid #c8dcee", borderRadius: 5,
                                    }}>
                                      <div style={{ fontSize: 9, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>
                                        📇 Contacts ({contacts.length})
                                      </div>
                                      {contacts.map((c, ci) => (
                                        <div key={ci} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginBottom: 2 }}>
                                          <span style={{ fontWeight: 600 }}>{c.name || "—"}</span>
                                          {c.role && <span style={{ color: "#888", fontSize: 10 }}>· {c.role}</span>}
                                          {c.phone && (
                                            <a
                                              href={`tel:${c.phone.replace(/[^0-9+]/g, "")}`}
                                              style={{ color: "#185FA5", textDecoration: "none", fontFamily: "monospace" }}
                                            >📞 {formatPhone(c.phone)}</a>
                                          )}
                                          {c.email && (
                                            <a
                                              href={`mailto:${c.email}`}
                                              style={{ color: "#185FA5", textDecoration: "none" }}
                                            >✉ {c.email}</a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                            {editing ? (
                              <select
                                value={stop.type || "pickup"}
                                onChange={(e) => updateStop(stop.id, "type", e.target.value)}
                                style={{ fontSize: 10, fontWeight: 700, padding: "2px 4px" }}
                                title="Fix an incorrectly-typed stop. Normally this is auto-set from jobType."
                              >
                                <option value="pickup">Pickup</option>
                                <option value="delivery">Delivery</option>
                                <option value="airport">✈ Airport</option>
                              </select>
                            ) : (
                              <span style={{
                                display: "inline-block", fontSize: 10, fontWeight: 700, padding: "2px 8px",
                                borderRadius: 5, background: typeColor.bg, color: typeColor.color,
                                textTransform: "uppercase", letterSpacing: 0.4,
                              }}>
                                {(() => {
                                  if (isAirport) return "✈ Airport";
                                  // For direct jobs label by route position (From / To /
                                  // Stop) — works for legacy data where every stop was
                                  // saved as `pickup`, and reflects the real semantic.
                                  if ((form.jobType || job.jobType) === "direct") {
                                    if (i === 0) return "From";
                                    if (i === stopsSrc.length - 1) return "To";
                                    return "Stop";
                                  }
                                  return stop.type;
                                })()}
                              </span>
                            )}
                            {/* Per-stop time only for inbound/outbound. Direct
                                jobs use one job-level scheduledTime instead. */}
                            {(form.jobType || job.jobType) !== "direct" && (
                              editing ? (
                                <input type="time"
                                  value={stop.scheduledTime || ""}
                                  onChange={(e) => updateStop(stop.id, "scheduledTime", e.target.value)}
                                  style={{ fontSize: 11, width: 95 }} />
                              ) : (stop.scheduledTime && (
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#111" }}>
                                  {stop.scheduledTime}
                                </div>
                              ))
                            )}
                            {editing && (
                              <div style={{ display: "flex", gap: 3 }}>
                                <button className="btn btn-sm" disabled={!canMoveUp}
                                  onClick={() => moveStop(i, -1)} title="Move up"
                                  style={{ padding: "2px 6px", fontSize: 11 }}>↑</button>
                                <button className="btn btn-sm" disabled={!canMoveDown}
                                  onClick={() => moveStop(i, 1)} title="Move down"
                                  style={{ padding: "2px 6px", fontSize: 11 }}>↓</button>
                                {!isAirport && (
                                  <button className="btn btn-sm" onClick={() => removeStop(stop.id)}
                                    title="Remove stop"
                                    style={{ padding: "2px 6px", fontSize: 11, color: "#a32d2d", borderColor: "#e8c5c5" }}>✕</button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Nested packages (skip for airport stops; on phones
                            an empty package list is left out) */}
                        {!isAirport && !(phoneView && stopPkgs.length === 0) && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #e0e6f0", marginLeft: 32 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>
                                Packages ({stopPkgs.length})
                              </div>
                              {editing && (
                                <button className="btn btn-sm" onClick={() => addPackage(stop.id)}
                                  style={{ fontSize: 11, padding: "2px 8px" }}>
                                  + Add package
                                </button>
                              )}
                            </div>
                            {stopPkgs.length > 0 && (
                              <>
                                <div style={{
                                  display: "grid",
                                  gridTemplateColumns: editing ? "130px 1fr 80px 80px 22px" : "130px 1fr 80px 80px",
                                  gap: 8, fontSize: 10, fontWeight: 700, color: "#aaa", marginBottom: 3,
                                }}>
                                  <span>{pkgNumLabel}</span><span>Description</span><span>Declared $</span><span>Weight</span>
                                  {editing && <span />}
                                </div>
                                {stopPkgs.map((pkg) => (
                                  <div key={pkg.id} style={{
                                    display: "grid",
                                    gridTemplateColumns: editing ? "130px 1fr 80px 80px 22px" : "130px 1fr 80px 80px",
                                    gap: 8, fontSize: 12, padding: "3px 0", borderBottom: "1px solid #f5f5f5",
                                    alignItems: "center",
                                  }}>
                                    {editing ? (
                                      <>
                                        <input value={pkg.hawbNumber || ""}
                                          onChange={(e) => updatePackage(pkg.id, "hawbNumber", e.target.value)}
                                          placeholder={pkgLabel}
                                          style={{ fontSize: 12, fontWeight: 600, color: "#185FA5" }} />
                                        <input value={pkg.description || ""}
                                          onChange={(e) => updatePackage(pkg.id, "description", e.target.value)}
                                          placeholder="Description"
                                          style={{ fontSize: 12 }} />
                                        <input type="number" value={pkg.declaredValue || ""}
                                          onChange={(e) => updatePackage(pkg.id, "declaredValue", e.target.value)}
                                          placeholder="$"
                                          style={{ fontSize: 12 }} />
                                        <input value={pkg.weight || ""}
                                          onChange={(e) => updatePackage(pkg.id, "weight", e.target.value)}
                                          placeholder="Weight"
                                          style={{ fontSize: 12 }} />
                                        <button onClick={() => removePackage(pkg.id)} title="Remove"
                                          style={{ background: "none", border: "none", cursor: "pointer", color: "#a32d2d", fontSize: 14, padding: 0 }}>
                                          ✕
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <span style={{ fontWeight: 600, color: "#185FA5" }}>{pkg.hawbNumber || <span style={{ color: "#bbb", fontWeight: 400 }}>(none)</span>}</span>
                                        <span style={{ color: "#333" }}>{pkg.description || "—"}</span>
                                        <span style={{ color: "#555" }}>{pkg.declaredValue ? `$${Number(pkg.declaredValue).toLocaleString()}` : "—"}</span>
                                        <span style={{ color: "#555" }}>{pkg.weight || "—"}</span>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                            {stopPkgs.length === 0 && !editing && (
                              <div style={{ fontSize: 11, color: "#bbb", fontStyle: "italic" }}>
                                No packages
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {editing && (
                    <div style={{ padding: "10px 14px", display: "flex", justifyContent: "flex-end" }}>
                      <button className="btn btn-sm btn-blue" onClick={addStop}>+ Add stop</button>
                    </div>
                  )}

                  {/* Orphan packages */}
                  {orphans.length > 0 && (
                    <div style={{ padding: "10px 14px", background: "#fff8e6", borderTop: "1px solid #f5d88a" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#8a6100", marginBottom: 4 }}>
                        ⚠️ Packages not assigned to a stop ({orphans.length})
                      </div>
                      {orphans.map((pkg) => (
                        <div key={pkg.id} style={{
                          display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#633806",
                          padding: "3px 0",
                        }}>
                          <span style={{ flex: 1 }}>
                            {pkg.hawbNumber || `(no ${pkgLabel})`} — {pkg.description || "—"}
                          </span>
                          {editing && (
                            <>
                              <select value={pkg.stopId || ""}
                                onChange={(e) => reassignPackage(pkg.id, e.target.value)}
                                style={{ fontSize: 11 }}>
                                <option value="">Assign to stop…</option>
                                {stopsSrc.filter((s) => s.type !== "airport").map((s, idx) => (
                                  <option key={s.id} value={s.id}>
                                    {idx + 1}. {s.locationName || s.address || "Stop"}
                                  </option>
                                ))}
                              </select>
                              <button onClick={() => removePackage(pkg.id)}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "#a32d2d", fontSize: 13 }}>
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ══ GUARDS ══ */}
        {tab === "guards" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* # of guards selector / readout */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#f2f5fa", border: "1px solid #d8e0ec", borderRadius: 8,
              padding: "10px 14px",
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}># of guards required</div>
              {editing ? (
                <select value={form.numGuards || 1}
                  onChange={(e) => setField("numGuards", Number(e.target.value))}
                  style={{ fontSize: 13, fontWeight: 700 }}>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <div style={{ fontSize: 13, fontWeight: 700, color: "#185FA5" }}>
                  {job.numGuards || Math.max(1, (job.guardAssignments?.length || 1))}
                </div>
              )}
            </div>

            {displayAssignments.length === 0 && !editing && (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="empty-state-icon">👤</div>
                <div>No guards assigned — job is in pool</div>
                {canEdit && (
                  <button className="btn btn-sm btn-blue" onClick={() => setEditing(true)}>
                    Assign a guard
                  </button>
                )}
              </div>
            )}

            {Array.from({ length: editing ? form.numGuards : (job.numGuards || 4) }, (_, i) => i + 1).map((gNum) => {
              const assignment = displayAssignments.find((g) => g.guardNumber === gNum);
              if (!assignment && !editing) return null;
              return (
                <div key={gNum} style={{ background: "#f8f9fb", border: "1px solid #e0e6f0", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
                    <div style={{ background: "#0B1C2E", color: "#fff", borderRadius: 6, padding: "3px 9px", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                      G{gNum}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}>
                      {guardOwnsCar(assignment, gNum) ? `G${gNum} — Own car · mileage` : `G${gNum} — Passenger · time only`}
                    </div>
                  </div>
                  {editing ? (
                    // Phones stack the guard picker over the pay rate so the
                    // rate box doesn't run off the right edge.
                    <div style={{ display: "grid", gridTemplateColumns: isPhone ? "1fr" : "1fr 130px", gap: 10 }}>
                      <select value={assignment?.guardId || ""} onChange={(e) => handleGuardAssign(gNum, e.target.value)}
                        style={isPhone ? { width: "100%", minWidth: 0, minHeight: 44, fontSize: 16 } : undefined}>
                        <option value="">Leave for pool...</option>
                        {guards.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}{g._isOwner ? " (owner)" : ""}
                          </option>
                        ))}
                      </select>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                        {isPhone && <span style={{ fontSize: 14, color: "#555", fontWeight: 600, marginRight: 6 }}>Pay rate</span>}
                        <span style={{ fontSize: 12, color: "#888", flexShrink: 0 }}>$</span>
                        <input type="number" inputMode="decimal" min={0} step={0.5} placeholder="$/hr"
                          value={assignment?.payRate || ""}
                          onChange={(e) => handlePayRate(gNum, e.target.value)}
                          style={isPhone
                            ? { width: 110, minHeight: 44, fontSize: 16, textAlign: "center" }
                            : { flex: 1, textAlign: "center" }} />
                        <span style={{ fontSize: 10, color: "#888", flexShrink: 0 }}>/hr</span>
                      </div>
                      {/* Vehicle — own car bills mileage, rides along is time
                          only. Only meaningful with 2+ guards; defaults to the
                          legacy lead/chase rule until changed. */}
                      {(form.numGuards || 1) > 1 && assignment?.guardId && (
                        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6 }}>
                          {[{ v: true, label: "🚗 Own car · mileage" }, { v: false, label: "🧍 Rides along · time only" }].map((opt) => {
                            const cur = guardOwnsCar(assignment, gNum);
                            const active = cur === opt.v;
                            return (
                              <button
                                key={String(opt.v)}
                                type="button"
                                onClick={() => handleVehicle(gNum, opt.v)}
                                style={{
                                  flex: 1,
                                  border: `1px solid ${active ? "#185FA5" : "#d8e0ec"}`,
                                  background: active ? "#eef3fb" : "#fff",
                                  color: active ? "#185FA5" : "#999",
                                  borderRadius: 7, padding: isPhone ? "11px 8px" : "6px 8px",
                                  fontSize: isPhone ? 13 : 11, fontWeight: 600, cursor: "pointer",
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 12, color: assignment ? "#111" : "#bbb", fontWeight: assignment ? 500 : 400 }}>
                        {assignment ? assignment.guardName : "Unassigned"}
                      </div>
                      {assignment?.payRate && (
                        <div style={{ fontSize: 12, color: "#555" }}>${assignment.payRate}/hr</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Who does which stop — lives here with the guard setup (not
                buried in the stops list) so it's findable. Everyone's on
                every stop by default; tap a guard to drop them from a stop.
                Field reality (who ACTUALLY does each stop) still wins for
                billing — this is the optional pre-assignment. */}
            {(editing ? form.numGuards : (job.numGuards || 1)) > 1 && (() => {
              const stopsSrc = (editing ? form.stops : job.stops) || [];
              const realStops = stopsSrc.filter((s) => s.type !== "airport");
              if (realStops.length === 0) return null;
              const gCount = editing ? form.numGuards : (job.numGuards || 1);
              return (
                <div style={{ background: "#fff", border: "1px solid #e0e6f0", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111", marginBottom: 4 }}>Who does which stop</div>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
                    {editing
                      ? "Everyone's on every stop by default. Tap a guard to drop them from a stop."
                      : "Tap Edit to change who covers each stop."}
                  </div>
                  {realStops.map((stop) => (
                    <div key={stop.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: "1px solid #f3f5f8" }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {stop.locationName || stop.address || "Stop"}
                      </div>
                      <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                        {Array.from({ length: gCount }, (_, k) => k + 1).map((n) => {
                          const cov = !stop.guardNumbers || stop.guardNumbers.length === 0 || stop.guardNumbers.includes(n);
                          return (
                            <button
                              key={n}
                              type="button"
                              disabled={!editing}
                              onClick={() => toggleStopGuard(stop, n)}
                              title={cov ? `G${n} is on this stop` : `G${n} is off this stop`}
                              style={{
                                border: `1px solid ${cov ? "#185FA5" : "#ccc"}`,
                                background: cov ? "#185FA5" : "#fff",
                                color: cov ? "#fff" : "#aaa",
                                borderRadius: 6, padding: "3px 10px",
                                fontSize: 11, fontWeight: 600,
                                cursor: editing ? "pointer" : "default",
                              }}
                            >
                              G{n}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* ══ AUDIT LOG ══ */}
        {tab === "log" && (
          <div>
            {auditLog.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="empty-state-icon">📋</div>
                <div>No audit entries yet</div>
              </div>
            ) : (
              <div className="panel">
                {auditLog.map((entry, i) => (
                  <div key={entry.id || i} style={{
                    padding: "10px 14px", borderBottom: "1px solid #f0f0f0",
                    display: "flex", gap: 10, alignItems: "flex-start",
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#185FA5", flexShrink: 0, marginTop: 5 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: "#111", lineHeight: 1.5 }}>{entry.action}</div>
                      <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
                        {entry.userName} · {fmtLog(entry.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer actions — phones: Reschedule, the job's next step, and ⋯
          (a bottom sheet holding Cancel, Delete and the rarer actions). */}
      {!editing && isPhone && (() => {
        const required = Number(job.numGuards) || (job.guardAssignments?.length || 1);
        const assigned = (job.guardAssignments || []).filter((g) => g.guardId || g.guardName).length;
        const big = { flex: 1, minHeight: 44, fontSize: 14, fontWeight: 700 };
        const item = { padding: "14px 20px", borderBottom: "1px solid #f0f2f5", fontSize: 15, cursor: "pointer" };
        const pick = (fn) => () => { setMoreOpen(false); fn(); };
        const canSplit = (job.numGuards || 1) > 1 && (job.stops || []).filter((s) => s.type !== "airport").length > 1;
        const canMerge = /-[AB]$/.test(job.jobNumber || "") && (job.subJobLabel === "Guard A" || job.subJobLabel === "Guard B");
        return (
          <>
            <div style={{ padding: "10px 12px", borderTop: "1px solid #e8e8e8", display: "flex", gap: 8, flexShrink: 0 }}>
              {canEdit && <button className="btn" style={big} onClick={() => setShowReschedule(true)}>Reschedule</button>}
              {canEdit && job.status === "intake" && <button className="btn btn-green" style={big} onClick={handleRelease}>Release to pool</button>}
              {canEdit && job.status === "grabbed" && <button className="btn btn-blue" style={big} onClick={handleConfirm}>Confirm guard</button>}
              {!["intake", "grabbed"].includes(job.status) && assigned < required && ["pool", "confirmed"].includes(job.status) && (
                <button className="btn btn-blue" style={big} onClick={() => setTab("guards")}>Assign guard</button>
              )}
              <button className="btn" style={{ ...big, flex: "0 0 48px" }} aria-label="More actions" onClick={() => setMoreOpen(true)}>⋯</button>
            </div>
            {moreOpen && (
              <div onClick={() => setMoreOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 250 }}>
                <div onClick={(e) => e.stopPropagation()} style={{
                  position: "absolute", left: 0, right: 0, bottom: 0, background: "#fff",
                  borderRadius: "18px 18px 0 0", paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
                }}>
                  <div style={{ width: 40, height: 5, background: "#d6dbe1", borderRadius: 3, margin: "10px auto 6px" }} />
                  {canEdit && (job.packages?.length || 0) > 0 && <div style={item} onClick={pick(() => setShowMovePkgs(true))}>Move packages…</div>}
                  {canEdit && canSplit && <div style={item} onClick={pick(() => setShowSplit(true))}>Split by guard…</div>}
                  {canEdit && canMerge && <div style={item} onClick={pick(handleMergeSplit)}>Merge back…</div>}
                  {canEdit && ["intake", "pool"].includes(job.status) && <div style={{ ...item, color: "#b3261e", fontWeight: 700 }} onClick={pick(handleCancel)}>Cancel job</div>}
                  {canRestore && <div style={{ ...item, color: "#2e7d32", fontWeight: 700 }} onClick={pick(handleRestore)}>Restore job (un-cancel)</div>}
                  <div style={{ ...item, color: "#b3261e", fontWeight: 700 }} onClick={pick(handleDelete)}>🗑 Delete job</div>
                  <div style={{ ...item, color: "#888", textAlign: "center", borderBottom: "none" }} onClick={() => setMoreOpen(false)}>Close</div>
                </div>
              </div>
            )}
          </>
        );
      })()}
      {!editing && !isPhone && (
        <div style={{ padding: "12px 20px", borderTop: "1px solid #e8e8e8", display: "flex", gap: 8, justifyContent: "space-between", flexShrink: 0, flexWrap: "wrap" }}>
          {/* Destructive — far left so it's visually separated from the
              constructive actions on the right. Test-data cleanup; uses
              a typed-confirmation prompt to prevent misclicks. */}
          <button
            onClick={handleDelete}
            title="Permanently delete this job (test data cleanup)"
            style={{
              background: "transparent", color: "#a32d2d",
              border: "1px solid #f5a5a5", borderRadius: 6,
              padding: "5px 10px", fontSize: 11, cursor: "pointer",
            }}
          >🗑 Delete</button>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canRestore && <button className="btn btn-green" onClick={handleRestore}>Restore job</button>}
            {canEdit && (
              <>
                <button className="btn btn-sm" onClick={() => setShowReschedule(true)}>Reschedule</button>
                {(job.packages?.length || 0) > 0 && (
                  <button className="btn btn-sm" onClick={() => setShowMovePkgs(true)}>
                    Move packages…
                  </button>
                )}
                {(job.numGuards || 1) > 1 && (job.stops || []).filter((s) => s.type !== "airport").length > 1 && (
                  <button className="btn btn-sm" onClick={() => setShowSplit(true)} title="Guards ran separate routes — split into -A and -B, one guard each">
                    Split by guard…
                  </button>
                )}
                {/-[AB]$/.test(job.jobNumber || "") && (job.subJobLabel === "Guard A" || job.subJobLabel === "Guard B") && (
                  <button className="btn btn-sm" onClick={handleMergeSplit} title="Undo the split — combine -A and -B back into one job">
                    Merge back…
                  </button>
                )}
                {job.status === "intake" && (
                  <button className="btn btn-green" onClick={handleRelease}>Release to pool</button>
                )}
                {job.status === "grabbed" && (
                  <button className="btn btn-blue" onClick={handleConfirm}>Confirm guard</button>
                )}
                {["intake", "pool"].includes(job.status) && (
                  <button className="btn btn-red" onClick={handleCancel}>Cancel job</button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showReschedule && (
        <RescheduleDialog
          job={job}
          onCancel={() => setShowReschedule(false)}
          onSubmit={handleReschedule}
        />
      )}
      {showMovePkgs && (
        <MovePackagesDialog
          job={job}
          onCancel={() => setShowMovePkgs(false)}
          onSubmit={handleMovePackages}
        />
      )}
      {showSplit && (
        <SplitByGuardDialog
          job={job}
          onCancel={() => setShowSplit(false)}
          onSubmit={handleSplitByGuard}
        />
      )}
    </Overlay>
  );
}

// ── Split by guard dialog ──────────────────────────────────
function SplitByGuardDialog({ job, onCancel, onSubmit }) {
  const stops = (job.stops || []).filter((s) => s.type !== "airport");
  const ga = job.guardAssignments || [];
  const nameA = ga[0]?.guardName || "Guard 1";
  const nameB = ga[1]?.guardName || "Guard 2";
  const [assign, setAssign] = useState(() => {
    const m = {};
    stops.forEach((s) => { m[s.id] = "A"; });
    return m;
  });
  const [saving, setSaving] = useState(false);
  const aCount = stops.filter((s) => assign[s.id] === "A").length;
  const bCount = stops.length - aCount;

  async function handleOk() {
    if (!aCount || !bCount) return;
    setSaving(true);
    try { await onSubmit(assign); } finally { setSaving(false); }
  }

  return (
    <DialogOverlay>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Split job by guard</div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 12 }}>
        Assign each stop to a guard. <b>{job.jobNumber}</b> becomes <b>{job.jobNumber}-A</b> ({nameA}); the B stops move to a new <b>{job.jobNumber}-B</b> ({nameB}). The airport stays on both. Each then bills its own time + mileage.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        {stops.map((s) => (
          <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 8px", border: "1px solid #eee", borderRadius: 6 }}>
            <span style={{ fontSize: 12, flex: 1, minWidth: 0 }}>{s.locationName || s.address || "Stop"}</span>
            <div style={{ display: "flex", gap: 4 }}>
              {["A", "B"].map((side) => (
                <button
                  key={side}
                  type="button"
                  onClick={() => setAssign((m) => ({ ...m, [s.id]: side }))}
                  style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 11px", borderRadius: 6, cursor: "pointer",
                    border: `1px solid ${assign[s.id] === side ? "#185FA5" : "#ddd"}`,
                    background: assign[s.id] === side ? "#185FA5" : "#fff",
                    color: assign[s.id] === side ? "#fff" : "#555",
                  }}
                >{side}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: (aCount && bCount) ? "#888" : "#a32d2d", marginBottom: 12 }}>
        A: {aCount} stop{aCount !== 1 ? "s" : ""} · B: {bCount} stop{bCount !== 1 ? "s" : ""}{(!aCount || !bCount) ? " — each guard needs at least one stop" : ""}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-sm btn-blue" onClick={handleOk} disabled={!aCount || !bCount || saving}>
          {saving ? "Splitting…" : "Split"}
        </button>
      </div>
    </DialogOverlay>
  );
}

// ── Reschedule dialog ──────────────────────────────────────
function RescheduleDialog({ job, onCancel, onSubmit }) {
  const [date, setDate] = useState(job.scheduledDate || "");
  const [time, setTime] = useState(job.scheduledTime || "");
  const [reason, setReason] = useState("");
  const [guardWent, setGuardWent] = useState(false);
  const [billBroker, setBillBroker] = useState(true);
  const [payGuard, setPayGuard] = useState(true);
  const [noShow, setNoShow] = useState(false);
  const [saving, setSaving] = useState(false);

  // A plain reschedule needs a date; a cancelled trip can be billed now with
  // the re-fly date unknown (left blank).
  const canSubmit = !!date || guardWent;
  async function handleOk() {
    if (!canSubmit) return;
    setSaving(true);
    try { await onSubmit(date, time, reason, guardWent, billBroker, payGuard, noShow); } finally { setSaving(false); }
  }

  return (
    <DialogOverlay>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Reschedule job</div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 14 }}>
        {guardWent
          ? `Bill ${job.jobNumber} as ${noShow ? "a no show" : "a cancelled trip"}. The job number is used up — a re-fly comes as a new dispatch.`
          : `Update the scheduled date/time for ${job.jobNumber}. Guard assignments, packages, and stops remain intact.`}
      </div>
      {!guardWent && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>
            New date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              style={{ width: "100%", marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>
            New time (optional)
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              style={{ width: "100%", marginTop: 4 }} />
          </label>
        </div>
      )}
      <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 12 }}>
        Reason (optional — saved to the audit log)
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. plane grounded — weather"
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>
      {/* Cancelled-trip billing: when the guard already drove to the airport
          before it cancelled, the original day is a billable trip. */}
      <label style={{
        display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 16,
        background: guardWent ? "#fff7e0" : "#fafbfc",
        border: `1px solid ${guardWent ? "#f0c878" : "#e0e6f0"}`,
        borderRadius: 7, padding: "9px 11px", cursor: "pointer",
      }}>
        <input type="checkbox" checked={guardWent} onChange={(e) => setGuardWent(e.target.checked)} style={{ marginTop: 2 }} />
        <span style={{ fontSize: 12, color: guardWent ? "#7a5400" : "#444" }}>
          <strong>Guard(s) went, then it was cancelled or a no show</strong> — bill this
          day as a cancelled trip (broker minimum, adjust in Ready to Bill). No re-fly
          job is created; if it flies again the dispatcher sends a new dispatch.
        </span>
      </label>
      {/* Independent choices for the cancelled trip. */}
      {guardWent && (
        <div style={{ margin: "-6px 0 16px 26px", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#444", marginBottom: 2 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="radio" checked={!noShow} onChange={() => setNoShow(false)} />
              Flight cancelled
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="radio" checked={noShow} onChange={() => setNoShow(true)} />
              No show (nobody there / shipment not ready)
            </label>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#444", cursor: "pointer" }}>
            <input type="checkbox" checked={billBroker} onChange={(e) => setBillBroker(e.target.checked)} />
            Bill the broker for this trip (adjust in Ready to Bill)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#444", cursor: "pointer" }}>
            <input type="checkbox" checked={payGuard} onChange={(e) => setPayGuard(e.target.checked)} />
            Pay the guard(s) for this trip
          </label>
          {!billBroker && !payGuard && (
            <div style={{ fontSize: 11, color: "#a36b1d" }}>
              Neither selected — the original day will just be cancelled (no charge, no pay).
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-sm btn-blue" onClick={handleOk} disabled={!canSubmit || saving}>
          {saving ? "Saving..." : (guardWent ? (noShow ? "Bill no show" : "Bill cancelled trip") : "Reschedule")}
        </button>
      </div>
    </DialogOverlay>
  );
}

// ── Move packages dialog ───────────────────────────────────
function MovePackagesDialog({ job, onCancel, onSubmit }) {
  const packages = job.packages || [];
  const stops    = job.stops    || [];
  const pkgLabel = hawbLabel(job);
  const [selected, setSelected] = useState(() => new Set());
  const [date, setDate] = useState("");
  const [time, setTime] = useState(job.scheduledTime || "");
  const [saving, setSaving] = useState(false);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAll() { setSelected(new Set(packages.map((p) => p.id))); }
  function selectNone() { setSelected(new Set()); }

  async function handleOk() {
    if (!selected.size || !date) return;
    setSaving(true);
    try { await onSubmit([...selected], date, time); } finally { setSaving(false); }
  }

  // Group packages by stop for display
  const pkgsByStop = stops.map((s) => ({
    stop: s,
    pkgs: packages.filter((p) => p.stopId === s.id),
  })).filter((g) => g.pkgs.length > 0);
  const orphans = packages.filter((p) => !p.stopId || !stops.find((s) => s.id === p.stopId));

  return (
    <DialogOverlay wide>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Move packages to a new job</div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        Select packages to move. A new job will be created on the chosen date with those packages
        and their associated stops. If nothing is left here, you'll be prompted to cancel this job.
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <button className="btn btn-sm" onClick={selectAll}>Select all</button>
        <button className="btn btn-sm" onClick={selectNone}>Clear</button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: "#888", alignSelf: "center" }}>
          {selected.size} of {packages.length} selected
        </div>
      </div>

      <div style={{
        maxHeight: 260, overflowY: "auto", border: "1px solid #e8e8e8", borderRadius: 6,
        padding: 8, marginBottom: 14, background: "#fafbfc",
      }}>
        {pkgsByStop.map(({ stop, pkgs }) => (
          <div key={stop.id} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
              {stop.locationName || stop.address || "Stop"}
            </div>
            {pkgs.map((p) => (
              <label key={p.id} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "4px 6px",
                fontSize: 12, cursor: "pointer", borderRadius: 4,
                background: selected.has(p.id) ? "#E6F1FB" : "transparent",
              }}>
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                <span style={{ fontWeight: 600, color: "#185FA5", minWidth: 110 }}>
                  {p.hawbNumber || `(no ${pkgLabel})`}
                </span>
                <span style={{ color: "#333", flex: 1 }}>{p.description || "—"}</span>
                <span style={{ color: "#666" }}>{p.declaredValue ? `$${Number(p.declaredValue).toLocaleString()}` : ""}</span>
              </label>
            ))}
          </div>
        ))}
        {orphans.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#8a6100", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
              Unassigned packages
            </div>
            {orphans.map((p) => (
              <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                <span style={{ fontWeight: 600, color: "#185FA5", minWidth: 110 }}>{p.hawbNumber || `(no ${pkgLabel})`}</span>
                <span style={{ color: "#333", flex: 1 }}>{p.description || "—"}</span>
              </label>
            ))}
          </div>
        )}
        {packages.length === 0 && (
          <div style={{ padding: 16, textAlign: "center", color: "#aaa", fontSize: 12 }}>No packages.</div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>
          New job date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ width: "100%", marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#666" }}>
          New job time (optional)
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
            style={{ width: "100%", marginTop: 4 }} />
        </label>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-sm btn-blue" onClick={handleOk}
          disabled={!selected.size || !date || saving}>
          {saving ? "Moving..." : `Move ${selected.size} to new job`}
        </button>
      </div>
    </DialogOverlay>
  );
}

function DialogOverlay({ children, wide }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: 20, width: "100%",
        maxWidth: wide ? 560 : 400, boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Overlay wrapper ───────────────────────────────────────
// modal-overlay / modal-card classes pick up the shared mobile treatment
// (full-screen card, stacked field rows) from OwnerLayout.css — same as
// JobCreateModal.
function Overlay({ onClose, children }) {
  return (
    <div className="modal-overlay" style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div className="modal-card" style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 700,
        maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.22)",
      }}>
        {children}
      </div>
    </div>
  );
}

// ── Field row ─────────────────────────────────────────────
// Inside a .field-grid parent, rows render as compact 2-up cells;
// `full` makes a row span both columns (button groups, banners).
// Per-stop route as billed in Review billing: each stop's time and the
// miles of the leg arriving there, then the return leg. Read-only — it
// shows what was saved, edits still happen in Ready to Bill. Hidden until
// the job has been through Review billing (no saved mileage legs).
function BillingBreakdown({ job }) {
  const stops = job.stops || [];
  const legs = job.mileageSegments;
  if (!Array.isArray(legs) || legs.length !== stops.length + 1) return null;
  const billable = Array.isArray(job.stopBillable) && job.stopBillable.length === stops.length
    ? job.stopBillable
    : stops.map(() => true);
  const times = Array.isArray(job.stopBillingTimes) ? job.stopBillingTimes : [];
  // Same totalling rule as Review billing: the first leg and the return
  // leg always count; a leg into an unbilled stop doesn't.
  const counted = (idx) => idx === 0 || idx === legs.length - 1 || billable[idx] !== false;
  const totalMiles = legs.reduce((sum, v, idx) => sum + (counted(idx) ? Number(v) || 0 : 0), 0);
  const hours = job.billingBreakdown?.hoursCharged;
  const cols = "28px minmax(0, 1fr) 70px 70px";
  const cell = { padding: "8px 16px", borderTop: "1px solid #f0f0f0", display: "grid", gridTemplateColumns: cols, gap: 10, alignItems: "center", fontSize: 13 };
  return (
    <div className="panel">
      <div className="panel-header"><div className="panel-title">🧭 Route &amp; mileage billed</div></div>
      <div style={{ ...cell, borderTop: "none", fontSize: 11, fontWeight: 700, color: "#888" }}>
        <span>#</span><span>Stop</span>
        <span style={{ textAlign: "right" }}>Time</span>
        <span style={{ textAlign: "right" }}>Miles</span>
      </div>
      {job.pickupStartTime && (
        <div style={cell}>
          <span />
          <span style={{ color: "#666" }}>Billing clock starts</span>
          <span style={{ textAlign: "right" }}>{job.pickupStartTime}</span>
          <span />
        </div>
      )}
      {stops.map((s, i) => {
        const billed = billable[i] !== false;
        return (
          <div key={s.id || i} style={{ ...cell, opacity: billed ? 1 : 0.5 }}>
            <span style={{ color: "#888" }}>{i + 1}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>{s.locationName || s.airportCode || s.address || "Stop"}</span>
              {!billed && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#a32d2d" }}>NOT BILLED</span>}
              <div style={{ fontSize: 11, color: "#888" }}>{s.type}{s.address ? ` · ${s.address}` : ""}</div>
            </span>
            <span style={{ textAlign: "right" }}>{times[i] || "—"}</span>
            <span style={{ textAlign: "right", textDecoration: counted(i) ? "none" : "line-through" }}>
              {Number(legs[i]) || 0}
            </span>
          </div>
        );
      })}
      <div style={cell}>
        <span>{job.returnedHome ? "🏠" : "↩"}</span>
        <span style={{ color: "#666" }}>{job.returnedHome ? "Return home" : "Return"}</span>
        <span style={{ textAlign: "right" }}>{job.returnTime || "—"}</span>
        <span style={{ textAlign: "right" }}>{Number(legs[legs.length - 1]) || 0}</span>
      </div>
      <div style={{ ...cell, background: "#eef4fa", fontWeight: 700, color: "#185FA5" }}>
        <span />
        <span>Totals</span>
        <span style={{ textAlign: "right" }}>{hours != null ? `${Number(hours).toFixed(2)}h` : "—"}</span>
        <span style={{ textAlign: "right" }}>{totalMiles} mi</span>
      </div>
    </div>
  );
}

// Mirrors the status chip colors on the Invoices page.
const INVOICE_STATUS_STYLE = {
  draft: { color: "#666", bg: "#f0f0f0" },
  sent:  { color: "#185FA5", bg: "#E6F0FA" },
  paid:  { color: "#0E7C46", bg: "#E0F4E8" },
  void:  { color: "#a32d2d", bg: "#FBE7E7" },
};

function Row({ label, children, full }) {
  return (
    <div className={`field-row${full ? " field-row-full" : ""}`}>
      <div className="field-label">{label}</div>
      <div className="field-value" style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

// Inline autocomplete for the Location name field on stop edit. Same
// behavior as JobCreateModal's StopRow — type to filter saved
// locations, picking one fills name/address/lat/lng/locationId
// atomically via the parent's onPick. Hooks into the locations list
// that the JobDetailModal already loads for the contacts feature.
function LocationNameAutocomplete({ value, locations, disabled, onChange, onPick }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = (value || "").toLowerCase();
  const matches = q.length >= 1
    ? (locations || [])
        .filter((l) =>
          (l.name || "").toLowerCase().includes(q) ||
          (l.customerName || "").toLowerCase().includes(q)
        )
        .slice()
        .sort((a, b) => (b.jobCount || 0) - (a.jobCount || 0))
        .slice(0, 12)
    : [];

  // Group matches by customerName so "Kay Jewelers" returns all its
  // store locations under one header.
  const groups = {};
  const ungrouped = [];
  matches.forEach((loc) => {
    if (loc.customerName) {
      if (!groups[loc.customerName]) groups[loc.customerName] = [];
      groups[loc.customerName].push(loc);
    } else {
      ungrouped.push(loc);
    }
  });
  const groupKeys = Object.keys(groups).sort();

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Location name (start typing to search saved)"
        disabled={disabled}
        autoComplete="off"
        style={{
          fontSize: 13, fontWeight: 600, width: "100%",
          background: disabled ? "#f0f0f0" : undefined,
        }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          marginTop: 2, background: "#fff",
          border: "1px solid #ddd", borderRadius: 7,
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          zIndex: 60, maxHeight: 240, overflowY: "auto",
        }}>
          {groupKeys.map((customerName) => (
            <React.Fragment key={customerName}>
              <div style={{
                padding: "5px 10px 2px", fontSize: 9, fontWeight: 800,
                color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5,
                background: "#f5f8ff", borderBottom: "1px solid #e8eef8",
              }}>
                {customerName}
              </div>
              {groups[customerName].map((loc) => (
                <div key={loc.id}
                  // onMouseDown so the input's onBlur doesn't close
                  // the popover before the selection registers.
                  onMouseDown={(e) => { e.preventDefault(); onPick(loc); setOpen(false); }}
                  style={{
                    padding: "6px 10px 6px 18px", cursor: "pointer",
                    fontSize: 11, borderBottom: "1px solid #f0f0f0",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f5f8ff"}
                  onMouseLeave={(e) => e.currentTarget.style.background = ""}
                >
                  <div style={{ fontWeight: 600 }}>{loc.name}</div>
                  {loc.address && (
                    <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{loc.address}</div>
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}
          {ungrouped.map((loc) => (
            <div key={loc.id}
              onMouseDown={(e) => { e.preventDefault(); onPick(loc); setOpen(false); }}
              style={{
                padding: "7px 10px", cursor: "pointer",
                fontSize: 11, borderBottom: "1px solid #f0f0f0",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f5f8ff"}
              onMouseLeave={(e) => e.currentTarget.style.background = ""}
            >
              <div style={{ fontWeight: 600 }}>{loc.name}</div>
              {loc.address && (
                <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{loc.address}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
